/**
 * Player character animation runtime (design/systems/character-animation-
 * runtime.md, Phase 1). Owns:
 *  - clip loading/caching (animation-only GLBs under `/animations/`)
 *  - one `THREE.AnimationMixer` per character instance, with two concurrent
 *    slots (legs / arms) on disjoint bone sets (assets/ASSETS.md) so they
 *    compose with no masking API
 *  - locomotion/pose state derivation from the sim's per-frame state
 *    (moving/gait/crouched/aiming/dialogOpen — read from index.ts's frame
 *    loop, which already tracks all of these) plus equipped-weapon kind
 *  - the weapon attach point (`hand.R`) and its equipment-driven swap
 *
 * Self-contained: subscribes to `gameStateStore` itself (equipment) rather
 * than requiring a caller to add subscription code — `index.ts`/
 * `location-view.ts` only call `createPlayerAnimator()` / `.update()` /
 * `.dispose()`.
 *
 * Deliberately NOT built on `character-select/load-model.ts`: that loader
 * (a) discards `gltf.animations` entirely, and (b) height-normalizes +
 * bottom-aligns against a real mesh bounding box — a bone-only animation GLB
 * has a degenerate (zero-height) box (divide-by-zero), and a held weapon's
 * "normalize to a target height, bottom-align to y=0" is simply the wrong
 * transform for a prop that should sit at a fixed grip offset instead. This
 * module has its own small loaders/caches for both.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import type { NpcActivity } from '../types';
import { gameStateStore } from '../../state/game-state';
import { BodyRegionMask, coveredRegions, drawnGarmentKeys } from './body-regions';
import { GARMENT_VARIANT_KEYS, garmentBaseKey, garmentColors } from './garment-variants';
import { buildBackpackModel, disposeBackpack, isBackpackKey } from './backpack-visuals';
import type { EquippedItemSummary, EquipmentSlot } from '../../state/game-state.types';

const ANIM_DIR = '/animations';
const WEAPON_DIR = '/models/weapons';
const ITEMS_DIR = '/models/items';
const GARMENT_DIR = '/models/garments';

// ─── Bone sets (assets/ASSETS.md) ───────────────────────────────────────────

// Raw glTF joint names, exactly as authored in Blender / present in the
// glTF JSON (verified against char-4-rigged.glb's own skins[0].joints this
// session). GLTFLoader sanitizes node names when building the scene graph
// AND when naming animation tracks (three's `PropertyBinding.sanitizeNodeName`,
// invoked via `GLTFLoader.createUniqueName` for every node) — so `hand.R`
// becomes the bone/track name `handR` at runtime, not `hand.R`. Running
// these raw lists through the SAME sanitize function, once, here — instead
// of hand-transcribing the sanitized spelling — is what keeps this module
// correct even if three's sanitize rule ever changes. Empirically verified
// against the real shipped clip files in character-animations.bones.test.ts.
const LEGS_BONES_RAW = [
  'root', 'hips', 'upper_leg.L', 'lower_leg.L', 'foot.L',
  'upper_leg.R', 'lower_leg.R', 'foot.R',
] as const;
const ARMS_BONES_RAW = [
  'spine', 'chest', 'neck', 'head', 'shoulder.L', 'upper_arm.L',
  'forearm.L', 'hand.L', 'shoulder.R', 'upper_arm.R', 'forearm.R', 'hand.R',
] as const;

export const sanitizeBoneName = (name: string): string => THREE.PropertyBinding.sanitizeNodeName(name);

/** LEGS_BONES, sanitized — root/hips/upper+lower leg/foot × L,R (8 bones). Exported for the standing test. */
export const LEGS_BONES: ReadonlySet<string> = new Set(LEGS_BONES_RAW.map(sanitizeBoneName));
/** ARMS_BONES, sanitized — spine/chest/neck/head/shoulder+upper_arm+forearm+hand × L,R (12 bones). Exported for the standing test. */
export const ARMS_BONES: ReadonlySet<string> = new Set(ARMS_BONES_RAW.map(sanitizeBoneName));
/** Sanitized `hand.R` — the weapon attach point. */
const HAND_R_BONE = sanitizeBoneName('hand.R');
const HEAD_BONE = sanitizeBoneName('head');
const CHEST_BONE = sanitizeBoneName('chest');

/**
 * Reduces a clip to only the tracks targeting `boneNames` — makes the LEGS/
 * ARMS disjointness invariant structural at runtime, not just trusted.
 * Needed because 3 shipped clips (`idle-1-breathing`/`idle-2-breathing-look`/
 * `idle-talking`, full-body files that predate the legs/arms split per
 * ASSETS.md) carry BOTH legs and arms tracks — assigning one whole,
 * un-stripped, to a single slot would leave two weight-1 `AnimationAction`s
 * fighting over the same quaternion tracks (they average ~50/50, neither
 * pose "wins"), not compose the way two properly split clips do. Applied to
 * every clip below (including already-pure `-legs`/`-arms` files, where it's
 * a harmless no-op filter) rather than only the 3 known full-body ones, so a
 * future re-export that silently regresses the split still can't leak
 * motion into the wrong slot.
 */
export function stripToBoneSet(clip: THREE.AnimationClip, boneNames: ReadonlySet<string>): THREE.AnimationClip {
  const tracks = clip.tracks.filter((t) => boneNames.has(t.name.split('.')[0] ?? ''));
  return new THREE.AnimationClip(clip.name, clip.duration, tracks);
}

// ─── Clip loading/caching ───────────────────────────────────────────────────

const clipLoader = new GLTFLoader();
const rawClipCache = new Map<string, Promise<THREE.AnimationClip>>();

/** Loads one animation-only GLB and extracts `gltf.animations[0]` (one clip per file, per the pipeline convention). Cached by filename — every character instance shares the same loaded clip data. */
function loadRawClip(name: string): Promise<THREE.AnimationClip> {
  let pending = rawClipCache.get(name);
  if (pending) return pending;
  pending = new Promise((resolve, reject) => {
    clipLoader.load(
      `${ANIM_DIR}/${name}.glb`,
      (gltf) => {
        const clip = gltf.animations[0];
        if (!clip) { reject(new Error(`[character-animations] "${name}.glb" has no animations[0]`)); return; }
        resolve(clip);
      },
      undefined,
      (err) => reject(err instanceof Error ? err : new Error(String(err))),
    );
  });
  rawClipCache.set(name, pending);
  return pending;
}

type LegsClipName =
  | 'idle-1-breathing' // full-body file (legs never move in it) — stripped to LEGS_BONES for the standing constant pose; see the doc comment on stripToBoneSet
  | 'walk-legs' | 'run-legs'
  | 'crouch-idle-legs' | 'crouch-move-legs'
  | 'stand-to-crouch-legs'
  /** NPC ambient activity (see createNpcAnimator). Single-keyframe static hold, like `crouch-idle-legs`. */
  | 'sit-idle-legs'
  /**
   * NPC `work` legs — the side-stepping half of the two-handed raking cycle
   * (2026-09-10). Split from ONE full-body Blender action with its arms
   * partner, like the idle-variety one-shots: the arms were IK-solved against
   * this clip's own hips sway, so the pair only composes back correctly when
   * played phase-locked (NpcAnimatorImpl starts both on its `cycleT` clock).
   */
  | 'work-raking-legs'
  /**
   * NPC `sleep` legs — the half that actually lays the body down: it rotates
   * `root` -90 deg about X (supine, head toward +Y) and rolls onto one side
   * once per ~8 s cycle. Unlike every other legs clip this one carries the
   * body's whole world orientation, so it must never be played against
   * another activity's arms.
   */
  | 'sleep-legs'
  // Idle-variety one-shots (see IDLE_VARIETY below) — legs half of a
  // synchronized legs+arms pair. `idle-4-look-around` has no legs file: the
  // legs stay on the constant `idle-1-breathing` pose while only the arms
  // and gaze move.
  | 'idle-3-stretch-legs' | 'idle-5-jump-legs' | 'idle-6-scuff-legs'
  /**
   * NPC `cheer` legs — a looping two-foot hop (`author_crowd_reactions.py`).
   * The only one of the three arena crowd reactions with a legs half at all:
   * its lift is `root`/`hips` TRANSLATION, which an arms clip may not carry
   * (`character-animations.channels.test.ts`). Split from one full-body
   * action with `cheer-arms`, so the pair is phase-locked like `sit` and
   * `work` — see `NPC_ACTIVITY_CLIPS.cheer`.
   */
  | 'cheer-legs'
  /**
   * NPC `death` legs — falls backward (root rotate + drop, knees buckling)
   * and ends supine, same end orientation as `sleep-legs` (see
   * author_death.py). Unlike every other legs clip, TERMINAL: played once
   * and held on its last frame, never `LoopRepeat` — see
   * `NPC_ACTIVITY_CLIPS.death`'s comment.
   */
  | 'death-legs';

type ArmsClipName =
  | 'idle-1-breathing-arms' | 'idle-2-breathing-look-arms'
  | 'idle-talking-arms'
  // Unarmed crouch hunch (2026-09-10 crouch rework): back bent, head down,
  // relaxed hanging arms. Unarmed only — the armed holds key chest/neck to 0
  // and their grips were derived against that, so an armed crouch keeps the
  // weapon hold's upright torso (deliberate; hunched armed holds would need
  // re-derived grips per family).
  | 'crouch-idle-arms'
  // Armed idle holds, one per pose family. `-plank-` is the BLUNT family's
  // file name (a pose-family name, not an item — `plank` itself is a MATERIAL
  // ItemDef); knife/axe were authored later as one-handed holds.
  | 'idle-armed-pistol-arms' | 'idle-armed-rifle-arms' | 'idle-armed-plank-arms'
  | 'idle-armed-knife-arms' | 'idle-armed-axe-arms'
  // Locomotion arms, per family. `-unarmed` predates the rest and keeps its
  // own suffix; the five armed families use the `-arms-<family>` spelling the
  // art pipeline settled on. All durations match their legs partner exactly
  // (verified from the shipped bytes), so pairs stay phase-locked.
  | 'walk-arms-unarmed' | 'run-arms-unarmed' | 'crouch-move-arms-unarmed'
  | 'walk-arms-pistol' | 'run-arms-pistol' | 'crouch-move-arms-pistol'
  | 'walk-arms-rifle' | 'run-arms-rifle' | 'crouch-move-arms-rifle'
  | 'walk-arms-blunt' | 'run-arms-blunt' | 'crouch-move-arms-blunt'
  | 'walk-arms-knife' | 'run-arms-knife' | 'crouch-move-arms-knife'
  | 'walk-arms-axe' | 'run-arms-axe' | 'crouch-move-arms-axe'
  // NPC ambient activities (see createNpcAnimator) — authored alongside the
  // player set but unreachable until an NPC had an animator at all.
  | 'sit-idle-arms' | 'work-raking-arms'
  // NPC 'aim' activity — rifle family only so far (see AIM_ARMS_CLIP).
  | 'aim-rifle-arms'
  // The pistol AIM hold — centred, both hands, raised, support hand over the
  // trigger guard (`author_pistol_holds.py`, 2026-09-19).
  | 'aim-pistol-arms'
  // NPC 'sleep' activity — supine hold with an occasional roll onto one side.
  | 'sleep-arms'
  // Arena crowd reactions (`author_crowd_reactions.py`, 2026-09-19): a fist
  // thrown up and out on every hop, three claps per loop, and a folded-arms
  // hold. `stand-crossed-arms` is spelled `<pose>-arms` like the rest — the
  // pose is "stand-crossed", the layer is "arms".
  | 'cheer-arms' | 'clap-arms' | 'stand-crossed-arms'
  // Idle-variety one-shots, fired at random while the (unarmed) player stands
  // idle — see IDLE_VARIETY and PlayerAnimatorImpl's idle-variety state.
  | 'idle-4-look-around-arms' | 'idle-3-stretch-arms' | 'idle-5-jump-arms' | 'idle-6-scuff-arms'
  // `hit` one-shot reaction (damage taken) — one per pose family, same
  // cardinality as the locomotion arms clips. See HIT_REACT_ARMS_CLIP and
  // author_hit_react.py. No trigger wires into this yet (no damage/combat
  // event exists in the client runtime) — `playHitReact()` is the API a
  // future combat system calls; verify it the way every other clip this
  // session got verified, by calling it against a map-authored test NPC.
  | 'hit-arms-unarmed' | 'hit-arms-pistol' | 'hit-arms-rifle'
  | 'hit-arms-knife' | 'hit-arms-axe' | 'hit-arms-blunt'
  // The bare-handed attack: a left jab into a right cross. Janeks asked for
  // a "fist fight animation ... when player has fists equipped" — there is no
  // `fists` ITEM, "fists" is the unarmed family.
  | 'attack-arms-unarmed'
  // NPC 'death' activity — arms drop to the sides as the body falls, same
  // terminal (never-looping) shape as death-legs.
  | 'death-arms'
  // `attack` one-shots — firearm recoil, melee swings, and the bare-handed
  // punch combo (author_attack_clips.py). One per pose family since
  // 2026-09-19, when `attack-arms-unarmed` closed the last gap; the record
  // stays Partial so a family added later cannot fail to compile before its
  // asset exists. Each clip is a short excursion that starts and ends on the
  // exact pose its weapon grip was solved against — see that script's docstring
  // for why that constraint exists and why `attack-arms-rifle` hangs off the
  // AIM pose rather than the low-ready carry.
  | 'attack-arms-rifle' | 'attack-arms-pistol'
  | 'attack-arms-knife' | 'attack-arms-axe' | 'attack-arms-blunt';

const LEGS_CLIP_NAMES: readonly LegsClipName[] = [
  'idle-1-breathing', 'walk-legs', 'run-legs', 'crouch-idle-legs', 'crouch-move-legs', 'stand-to-crouch-legs',
  // Idle-variety legs halves (see IDLE_VARIETY) — small, eagerly loaded like
  // the rest of this constant-pose/transition set.
  'idle-3-stretch-legs', 'idle-5-jump-legs', 'idle-6-scuff-legs',
];

/**
 * Arms clips fetched up front, at animator construction.
 *
 * Deliberately NOT every `ArmsClipName`: there are now 24 of them (~1 MB of
 * GLB), and 15 are locomotion clips for the five ARMED families. An unarmed
 * player — which every player is until they equip something — would otherwise
 * pay for axe, knife, blunt, pistol and rifle clips it may never show. This
 * set is exactly what an unarmed player can reach: both idle variants, the
 * dialog pose, and unarmed locomotion. Everything else loads on first use via
 * `ensureArmsAction`, which fits the slot's existing "not loaded yet — retried
 * next frame" behaviour with no new state machine.
 */
const EAGER_ARMS_CLIPS: readonly ArmsClipName[] = [
  'idle-1-breathing-arms', 'idle-2-breathing-look-arms', 'idle-talking-arms',
  'walk-arms-unarmed', 'run-arms-unarmed', 'crouch-move-arms-unarmed', 'crouch-idle-arms',
  // Idle-variety arms clips: small, and every unarmed player will eventually
  // trigger one (see IDLE_VARIETY) — same reasoning as the rest of this list.
  'idle-4-look-around-arms', 'idle-3-stretch-arms', 'idle-5-jump-arms', 'idle-6-scuff-arms',
];

async function loadLegsClip(name: LegsClipName): Promise<THREE.AnimationClip> {
  return stripToBoneSet(await loadRawClip(name), LEGS_BONES);
}
async function loadArmsClip(name: ArmsClipName): Promise<THREE.AnimationClip> {
  return stripToBoneSet(await loadRawClip(name), ARMS_BONES);
}

// ─── Prop model loading — weapons AND attachable equipment (headgear, etc.);
// NOT character-select/load-model.ts — see file header ───

const propLoader = new GLTFLoader();
const propRootCache = new Map<string, Promise<THREE.Object3D>>();

/**
 * Unwraps `gltf.scene`'s single child, when there is exactly one. GLTFLoader
 * always hands back `gltf.scene` as a synthetic identity-transform `Group`
 * wrapping whatever the file's own top-level node(s) are — for every prop
 * here (confirmed 2026-09-16: every `/models/weapons/*.glb` has exactly one
 * top-level node) that wrapper is NOT the authored root node, it's an extra
 * layer ABOVE it. The real root-node `scale` this codebase's whole
 * scale-contract (`weapon-scale.test.ts`'s `rootScale()`, every
 * `WEAPON_GRIP_OFFSET` comment) assumes lives on that inner node, not on the
 * wrapper. Left unfixed, every caller that reads/sets `.scale`/`.position`/
 * `.rotation` on "the model" — most critically `attachToBone`, which used to
 * multiply the rig-cancel factor against the wrapper's permanently-identity
 * scale — silently ignores whatever real-world size was authored, and every
 * prop collapses to the SAME size regardless of its GLB (the "every weapon
 * tested is somewhere it shouldn't be" report). A multi-node file (none
 * exist today) falls back to the old wrapper behaviour rather than guessing
 * which child is "the" root.
 */
function unwrapSingleNodeScene(scene: THREE.Object3D): THREE.Object3D {
  return scene.children.length === 1 ? scene.children[0]! : scene;
}

function loadPropRoot(url: string): Promise<THREE.Object3D> {
  let pending = propRootCache.get(url);
  if (pending) return pending;
  pending = new Promise((resolve, reject) => {
    propLoader.load(url, (gltf) => resolve(unwrapSingleNodeScene(gltf.scene)), undefined, (err) => reject(err instanceof Error ? err : new Error(String(err))));
  });
  propRootCache.set(url, pending);
  return pending;
}

/** Weapon props carry no skeleton (confirmed this session — every `/models/weapons/*.glb` parses with `skins: 0`), so a plain clone per equip is safe and cheap. */
async function loadPropModel(url: string): Promise<THREE.Object3D> {
  const root = await loadPropRoot(url);
  return root.clone();
}

// ─── Garments: skinned clothing, driven by the character's OWN skeleton ─────
//
// Everything above this line attaches a prop RIGIDLY to one bone. That is
// correct for a weapon, a cap, or a plate carrier, and wrong for anything with
// a sleeve or a trouser leg: the rigid path can only follow a single bone, and
// the bones a garment would need to follow (`upper_arm.L/R`, `upper_leg.L/R`,
// …) are exactly the ones that ANIMATE. A sleeve parented to `chest` hangs in
// the air the moment an arm swings.
//
// A garment is therefore not a prop at all — it is a second skinned mesh on
// the same skeleton, exactly like the character's own body mesh. The only
// thing standing in the way is that a GLB arrives carrying its OWN copy of the
// bones (GLTFLoader builds a fresh `Bone` per joint and a fresh `Skeleton`),
// so dropped in as-is it deforms against invisible bones nothing drives, and
// visually never moves. `bindGarmentToSkeleton` re-points it at the
// character's bones. That is the entire mechanism.
//
// ── Naming convention (so adding a garment needs no new code) ───────────────
//
// A garment's GLB is `/models/garments/<itemKey>.glb` — the file stem IS the
// ItemDef key. No per-item URL row, no bone field, and (unlike every table
// above) NO GRIP OFFSET: skinning places the geometry, so there is nothing to
// derive and nothing to tune. Adding a garment is:
//
//   1. drop `<itemKey>.glb` into `frontend/public/models/garments/`
//   2. add `<itemKey>` to `GARMENT_KEYS` below
//   3. seed the ItemDef
//
// Step 2 is deliberately not automated away. `public/` is copied verbatim and
// is not visible to `import.meta.glob`, so the alternative is requesting a URL
// that may 404 on every equip. Instead `character-animations.garments.test.ts`
// cross-checks this list against the directory IN BOTH DIRECTIONS — a file
// with no key, or a key with no file, fails the build. You cannot half-add a
// garment and discover it silently doesn't render.
//
// Authoring requirements, enforced by that same test against the shipped
// bytes: the GLB must carry a skin, and its joints must be a SUBSET of the
// shared 20-bone rig's names (a garment may skin to 4 bones and ignore the
// rest — it may not invent one). Author it in char-4's own space and weight it
// with `assets/blender/scripts/auto_rig.py`'s `skin()`, the same function that
// rigged all 23 characters; see `garment_build.py`.

/** The premium line (RARE): four garments for each skinned slot. */
export const PREMIUM_GARMENT_KEYS: readonly string[] = [
  'torso_operator', 'torso_carbon-bomber', 'torso_moto-leather', 'torso_prism-windbreaker',
  'vest_carbon', 'vest_kevlar', 'vest_chest-rig', 'vest_gilded',
  'legs_operator', 'legs_carbon', 'legs_moto', 'legs_desert-ranger',
  'glove_operator', 'glove_carbon', 'glove_moto', 'glove_kevlar',
  'feet_operator', 'feet_carbon', 'feet_moto', 'feet_desert',
  // Golden Set (Atelier Hollis, 2026-10-17), with vest_gilded above.
  'torso_brocade-frock', 'legs_gilt-stripe', 'glove_gilded', 'feet_gilt-buckle',
];

/** The five themed sets. Each set's headgear is a rigid prop (EQUIPMENT_MODEL_BY_KEY). */
export const SET_GARMENT_KEYS: readonly string[] = [
  'torso_army-bdu', 'legs_army-bdu', 'glove_army', 'feet_army-jungle', 'vest_army-flak',
  'torso_tactical-combat', 'legs_tactical-urban', 'glove_tactical', 'feet_tactical', 'vest_tactical-carrier',
  'torso_survivor-patched', 'legs_survivor-waxed', 'glove_survivor', 'feet_survivor',
  'torso_formal-suit', 'legs_formal-suit', 'glove_formal', 'feet_formal-oxford',
  'torso_nerd-argyle', 'legs_nerd-cords', 'glove_nerd', 'feet_nerd-sneakers',
];

/**
 * Item keys that have a skinned garment MESH — one GLB each. See the
 * convention above — the URL is derived from the key, so this list is the only
 * place a new garment is named. Colour variants are not listed here: they
 * share a base's mesh (see `garment-variants.ts`).
 */
export const BASE_GARMENT_KEYS: readonly string[] = [
  // TORSO — sleeved, which is what the rigid `chest` attach cannot do.
  'torso_jacket',
  'torso_leather',
  'torso_lumbershirt',
  'torso_puffer',
  'torso_raincoat',
  'torso_tactical-hoodie',
  'torso_trench',
  // VEST — armour worn over a torso garment, skinned to hips/spine/chest.
  // Hidden under torso_raincoat / torso_trench (body-regions.ts's drawnGarmentKeys).
  'vest_light',
  'plate_vest',
  // LEGS — thigh + shin tubes, blended at the knee.
  'legs_jeans',
  'legs_tactical',
  'legs_cargo',
  'legs_shorts-cargo',
  'legs_sport-short',
  // GLOVES — one bone per side; a hand has no joints below the wrist here.
  'glove_mitten',
  // FEET — foot shell on `foot`, shaft on `lower_leg`. Equippable through the
  // FEET slot; NPCs and bots wear it through `equippedKeys` and `test_dummies`.
  'feet_boots',
  // TEXTURED (2026-10-16, premium_garment_build.py) — the premium line, four
  // per slot, then the army / tactical / survivor / formal / nerd sets. Same
  // skinning; the materials sample Higgsfield swatches embedded as WebP.
  ...PREMIUM_GARMENT_KEYS,
  ...SET_GARMENT_KEYS,
];

/**
 * Every wearable garment key: the bases plus their colour variants. A variant
 * has no file of its own, so the file cross-check in
 * `character-animations.garments.test.ts` runs over `BASE_GARMENT_KEYS`.
 */
export const GARMENT_KEYS: readonly string[] = [...BASE_GARMENT_KEYS, ...GARMENT_VARIANT_KEYS];

/**
 * Slots checked for a garment. HEADGEAR is deliberately absent: a cap is a
 * rigid object on a bone that doesn't animate, so `EQUIPMENT_MODEL_BY_KEY`
 * remains the right mechanism for it and skinning would only add cost.
 *
 * FEET is a garment slot like the others: boots are skinned to the foot and
 * shin, so they follow the legs clip the way trousers do.
 */
const GARMENT_SLOTS: readonly EquipmentSlot[] = ['TORSO', 'VEST', 'LEGS', 'GLOVES', 'FEET'];

/** `/models/garments/<key>.glb` — the whole URL scheme, in one place. A colour variant resolves to its base's file. */
export function garmentUrl(key: string): string {
  return `${GARMENT_DIR}/${garmentBaseKey(key)}.glb`;
}

/**
 * Recolours a colour variant's mesh. Materials are cloned first:
 * `SkeletonUtils.clone` shares them with the cached parse, so writing a colour
 * into one would recolour every character wearing that base garment. A base
 * garment (no overrides) is left untouched.
 */
export function applyGarmentColors(garment: THREE.Object3D, key: string): void {
  const colors = garmentColors(key);
  if (!colors) return;
  garment.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) recolorMesh(o as THREE.Mesh, colors);
  });
}

function recolorMesh(garment: THREE.Mesh, colors: NonNullable<ReturnType<typeof garmentColors>>): void {
  const recolor = (material: THREE.Material): THREE.Material => {
    const rgb = colors[material.name];
    if (!rgb) return material;
    const copy = material.clone() as THREE.MeshStandardMaterial;
    copy.color.setRGB(rgb[0], rgb[1], rgb[2]);
    return copy;
  };
  garment.material = Array.isArray(garment.material)
    ? garment.material.map(recolor)
    : recolor(garment.material);
}

const garmentRootCache = new Map<string, Promise<THREE.Object3D>>();

/**
 * Loads a garment GLB and caches the parsed scene. Kept separate from
 * `loadPropRoot` because that one runs `unwrapSingleNodeScene`, which is
 * actively wrong here: a skinned file's scene holds the mesh AND the armature
 * root, so it has more than one child and there is no single "authored root
 * node" to unwrap to.
 */
function loadGarmentRoot(url: string): Promise<THREE.Object3D> {
  let pending = garmentRootCache.get(url);
  if (pending) return pending;
  pending = new Promise((resolve, reject) => {
    propLoader.load(url, (gltf) => resolve(gltf.scene), undefined,
      (err) => reject(err instanceof Error ? err : new Error(String(err))));
  });
  garmentRootCache.set(url, pending);
  return pending;
}

/**
 * Re-points a freshly-loaded garment's `SkinnedMesh` at `target`'s bones, so
 * the character's animation drives it.
 *
 * Returns a group of meshes to add to the character root, or null if the file
 * carries no skinned mesh or names a joint this rig doesn't have.
 *
 * **Every primitive, not the first.** GLTFLoader turns a multi-material mesh
 * into a Group of one SkinnedMesh per primitive, and every shipped garment has
 * 2-9 materials. Keeping only the first dropped all trims, and on
 * `torso_nerd-argyle` (argyle body first, oxford sleeves third) it dropped the
 * sleeves while the TORSO cover still hid the arms: hands floating in air.
 *
 * Three details that are easy to get wrong, all load-bearing:
 *
 * 1. **Build the new `Skeleton` in the GARMENT's joint order**, not the
 *    character's. `skinIndex` in the garment's geometry indexes ITS array; a
 *    skeleton assembled in any other order silently weights each vertex to the
 *    wrong bone — geometry that moves, plausibly, and wrongly.
 * 2. **Reuse the garment's own `boneInverses`**, not the character's. They are
 *    the inverse BIND matrices for the pose the garment was authored in. They
 *    happen to be near-identical here because every rig comes off the same
 *    template, but "near" is not "equal", and taking the character's would
 *    bake its per-character rest-pose difference into the garment as a shear.
 * 3. **Parent to the character ROOT, not to a bone.** A skinned mesh is
 *    positioned entirely by its skeleton; putting it under a bone would apply
 *    that bone's transform a second time. This is also why no grip offset
 *    exists, and why the rig's 1.8035 normalization scale needs no cancelling
 *    the way `attachToBone` has to cancel it for rigid props — the garment is
 *    under the same scaled root as the body it covers.
 */
export function bindGarmentToSkeleton(loaded: THREE.Object3D, target: THREE.Skeleton): THREE.Group | null {
  const meshes: THREE.SkinnedMesh[] = [];
  loaded.traverse((o) => {
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) meshes.push(o as THREE.SkinnedMesh);
  });
  if (meshes.length === 0) {
    console.warn('[character-animations] garment GLB carries no SkinnedMesh — it will not render');
    return null;
  }

  // One glTF skin, so every primitive's skeleton lists the same joints in the
  // same order; one rebound Skeleton serves them all.
  const source = meshes[0]!.skeleton;
  const bones: THREE.Bone[] = [];
  for (const own of source.bones) {
    const match = target.getBoneByName(own.name);
    if (!match) {
      console.warn(`[character-animations] garment names a joint "${own.name}" this rig does not have — not rendering it`);
      return null;
    }
    bones.push(match);
  }
  const skeleton = new THREE.Skeleton(bones, source.boneInverses);

  const garment = new THREE.Group();
  for (const mesh of meshes) {
    mesh.bind(skeleton, mesh.bindMatrix);
    // The loader parents the mesh under the file's own armature node; detach
    // it so adding it to the character root doesn't drag that node (and the
    // now-orphaned duplicate bones) along with it.
    mesh.removeFromParent();
    garment.add(mesh);
  }
  return garment;
}

/**
 * Parents `model` to `bone` and cancels the character rig's own runtime
 * normalization scale, so a weapon authored at real-world scale doesn't
 * inherit it and render oversized. `character-select/load-model.ts` scales the
 * whole rig by `targetHeight / bboxHeight` = 1.8 / 0.998 = **1.8035** (both
 * figures measured from char-4-rigged.glb's own POSITION accessors; an
 * earlier version of this comment said "~1.9x against a ~0.95-unit height",
 * which was wrong by ~5%). Nothing here depends on the constant — the scale is
 * read from `getWorldScale` at runtime — but don't propagate the old number
 * into anything that does. Assumes uniform parent scale, true for this rig
 * (bones aren't individually scaled).
 *
 * **Must divide the model's OWN scale, never overwrite it.** Found
 * 2026-09-16: this used to `setScalar(1 / parentWorldScale.x)` outright,
 * discarding whatever scale `model` already carried. That was correct back
 * when weapon scale was baked into vertex positions (root node reset to
 * identity) — dividing an identity scale by the rig factor and overwriting
 * with it are the same operation. It silently broke the moment this
 * session's scale-contract fix (see `WEAPON_GRIP_OFFSET`'s own doc comment)
 * switched every weapon to carrying its real-world size as a non-1 root-node
 * `scale` instead: every weapon was then forced to the SAME
 * `1/parentWorldScale.x` (~0.53–0.55 for this rig) regardless of its actual
 * authored size, so every grip offset this entire session was tuned in
 * Blender against a real-world-sized weapon that the runtime then rendered
 * at a completely unrelated, uniform size — the "weapon is somewhere it
 * shouldn't be, true of every weapon tested" report. Caught by inspecting a
 * live running NPC's actual attached-weapon scale, not by any test: nothing
 * in this codebase asserts what `attachToBone` does to a real weapon prop's
 * scale, only what the GLB file itself carries on disk
 * (`weapon-scale.test.ts`) or what its authored offsets are.
 *
 * **A grip's `position` offset must NOT be divided the same way.** Scale and
 * position are cancelled at different points, on purpose:
 *
 *   * The weapon's GEOMETRY carries real-world metres (root-node scale). The
 *     bone's world scale would multiply that, so it is divided out here —
 *     leaving the geometry at metres in world, i.e. at 1/1.8035 of a rig unit
 *     per metre inside the bone's local space.
 *   * A grip's POSITION is already a bone-local vector in RIG units (it is
 *     `PALM_LOCAL - R·handle`, and `PALM_LOCAL` is a hand-local point read
 *     straight off the body's own POSITION accessors). A bone-local child
 *     transform is in rig units by definition, so it is set unchanged. The
 *     bone's world scale multiplying it is the whole point: that is what puts
 *     it at the same world metres as the hand it was measured against.
 *
 * A `scaledGripPosition` helper lived here from 2026-09-16 to 2026-09-18 and
 * divided the position too. **That was the bug behind "the weapons are not
 * properly attached to the hands".** It shrank every grip to ~1/1.8 of its
 * authored length while the geometry beside it kept full size, so the handle
 * left the palm by `|position|·(1 - 1/k)` — measured over all 41 shipped rows
 * against all 8 player bodies: 112-138 mm average, 590 mm worst case
 * (`hockey_stick`). On `char-4-rigged`, the body the table is solved against,
 * removing it puts every derived handle centroid back on `PALM_LOCAL` to
 * 0.0 mm; keeping it left them 123 mm off on average.
 *
 * The evidence that originally argued FOR the division was a misread: a
 * shotgun rendering "~0.43 world-units from `hand.R`, down near the ankle"
 * is the weapon's ROOT, not its handle. A 1.05 m shotgun held at the grip
 * has its root roughly half a metre from the hand — 0.43 is close to the
 * CORRECT value, and the handle was in the palm at the time. Measure
 * handle-centroid-to-palm, never root-to-bone; `weapon-grip.test.ts` does.
 */
function attachToBone(bone: THREE.Bone, model: THREE.Object3D): void {
  const parentWorldScale = new THREE.Vector3();
  bone.getWorldScale(parentWorldScale);
  const authoredScale = model.scale.x;
  bone.add(model);
  model.scale.setScalar(authoredScale / (parentWorldScale.x || 1));
  // Stashed so a later detach (dropping a dead NPC's weapon on the ground —
  // see NpcAnimatorImpl's death handling) can restore real-world scale
  // directly instead of re-deriving `authoredScale / newParentWorldScale`
  // against whatever the new parent's scale happens to be.
  model.userData.authoredScale = authoredScale;
}

/**
 * `char-4-rigged.glb`'s own hand-weighted vertex centroid, in `hand.R`-local
 * rig units, and that body's rig normalization scale (1.8 / 0.998).
 *
 * Every row in `WEAPON_GRIP_OFFSET` is `PALM - R·handle` solved against THIS
 * body — see `weapon_grip.PALM_LOCAL`, which this duplicates deliberately
 * (`weapon-grip.test.ts` re-measures it from the shipped GLB and fails if the
 * two drift). `char-4-rigged` is not a body any player can pick: it is the
 * superseded pre-2026-09-09 mesh, kept as the solver's reference frame.
 */
export const REFERENCE_PALM_LOCAL = new THREE.Vector3(-0.032, 0.0511, 0.0281);
export const REFERENCE_RIG_SCALE = 1.8 / 0.998;

/**
 * Centroid of the vertices a body actually weights to `bone`, in that bone's
 * own local (bind) space — i.e. the same measurement `REFERENCE_PALM_LOCAL`
 * is, taken against whatever body is really on screen.
 *
 * Needed because the eight player presets do NOT put `hand.R` in the same
 * place inside their own hand mesh as the reference body does. Measured
 * 2026-09-18 across all eight: 20 to 36 mm apart at render scale, char-2 and
 * char-4 in opposite directions. The 2026-09-17 re-rig closed arm, leg and
 * torso placement to 0.1mm but has no hand landmark, so this survives it.
 *
 * Returns null when nothing is weighted to the bone (>0.5, i.e. the bone is
 * that vertex's dominant influence), which is a real case — `chest` carries
 * zero verts on bodies whose torso has no mid loop. Callers fall back to the
 * authored row, which is the reference body's answer.
 */
export function measureBoneLocalCentroid(skinned: THREE.SkinnedMesh, bone: THREE.Bone): THREE.Vector3 | null {
  const index = skinned.skeleton.bones.indexOf(bone);
  const inverse = skinned.skeleton.boneInverses[index];
  const position = skinned.geometry.getAttribute('position');
  const skinIndex = skinned.geometry.getAttribute('skinIndex');
  const skinWeight = skinned.geometry.getAttribute('skinWeight');
  if (index < 0 || !inverse || !position || !skinIndex || !skinWeight) return null;

  const sum = new THREE.Vector3();
  const v = new THREE.Vector3();
  let n = 0;
  for (let i = 0; i < position.count; i++) {
    let w = 0;
    for (let c = 0; c < 4; c++) if (skinIndex.getComponent(i, c) === index) w += skinWeight.getComponent(i, c);
    if (w <= 0.5) continue;
    sum.add(v.fromBufferAttribute(position, i).applyMatrix4(inverse));
    n++;
  }
  return n > 0 ? sum.divideScalar(n) : null;
}

/**
 * The authored grip, re-anchored onto the palm of the body actually wearing it.
 *
 * A row is `palm - handleOffset`, both in the REFERENCE body's rig units. Two
 * things differ on a real body, and they pull in opposite directions:
 *
 *  - its palm sits somewhere else in `hand.R`-local space (`palm`), and
 *  - its rig units are a different size, while the weapon still renders at
 *    fixed real-world metres — so the handle's offset from the weapon root
 *    measures differently in those units (`REFERENCE_RIG_SCALE / scale`).
 *
 * Reconstructing the row from its two halves and re-composing handles both.
 * On the reference body this returns the authored numbers unchanged, which is
 * what keeps `derive_grips_gltf.py` the single source of truth.
 */
export function anchoredGripPosition(
  position: readonly [number, number, number],
  palm: THREE.Vector3 | null,
  bone: THREE.Bone,
): readonly [number, number, number] {
  if (!palm) return position;
  const worldScale = new THREE.Vector3();
  bone.getWorldScale(worldScale);
  const scale = worldScale.x || REFERENCE_RIG_SCALE;
  const handle = REFERENCE_PALM_LOCAL.clone().sub(new THREE.Vector3(...position));
  const anchored = palm.clone().addScaledVector(handle, -REFERENCE_RIG_SCALE / scale);
  return [anchored.x, anchored.y, anchored.z];
}

/**
 * The rigid-attach counterpart to {@link anchoredGripPosition}, for a prop
 * whose offset is a fixed REAL-WORLD distance from its bone rather than a
 * point on the body's surface: headgear and the torso vests.
 *
 * Three conventions are in play in this file and they are not
 * interchangeable — picking the wrong one moves the prop by tens of
 * centimetres:
 *
 *  - a weapon's grip: a point on the HAND, so it follows each body's own palm
 *    (`anchoredGripPosition`);
 *  - a cap or a vest: a fixed distance from a bone, in metres. Every body
 *    renders at 1.8 m with `head` at exactly 0.992 m and a head 779-807 mm
 *    tall (measured across all eight presets, 2026-09-18), so "69 cm above the
 *    head bone" is the same place on every one of them, while the same number
 *    read as rig units drifts up to 79 mm on the shorter-native bodies;
 *  - the weapon GEOMETRY, which is metres for a different reason (it is a real
 *    object, not a placement) — `attachToBone`.
 *
 * The rows are authored in the reference body's rig units (see
 * `verify_cap_offset.py`: `b2g(CENTER_W) - W_head.translation`), so converting
 * to metres and back into this body's units is one factor.
 *
 * Until 2026-09-18 these were divided by the bone's world scale alone, i.e.
 * treated as metres when they are rig units — 1.8x too short. `cap_baseball`
 * rendered at 1.38 m on a 1.8 m character: on the chin, not on the head.
 */
/**
 * Per-body headgear fit, 2026-10-17.
 *
 * Every hat is authored on a head whose crown is 0.448 reference rig units above
 * the `head` joint, and it renders at fixed metres. That holds for 21 of the 23
 * bodies. On Rook (char-6) the tallest point of the head is a ponytail, so once
 * the body is normalised to 1.8 m its real crown sits at 0.323 and every hat
 * floated a hand's width above the hair (Janeks: "Party hat is not properly
 * seated on the Rook player model"). npc-survivor-8's hood does the same.
 *
 * The fix measures the crown on the body itself: the highest head-weighted
 * vertex no more than HEAD_BACK_CUTOFF behind the joint (a ponytail, a bun or a
 * hood hangs further back than that), and scales the prop about the head joint
 * by crown / 0.448 vertically (width and depth: `headgearFit`). The midline is
 * NOT used for the crown: the low-poly box heads (Ash) have
 * no vertex on the midline at the crown, only at the corners.
 * `verify_hat_fit.head_fit` is the Blender mirror; `headgear-fit.test.ts` pins
 * both to the shipped bodies.
 */
export const HEAD_CROWN_REF = 0.448;
export const HEAD_BACK_CUTOFF = 0.10;
const HEADGEAR_FIT_MIN = 0.6;
const HEADGEAR_FIT_MAX = 1.1;
const HEADGEAR_FIT_DEADBAND = 0.05; // the 21 standard bodies measure 0.98-1.0: leave their verified fits alone

/** Head-dominated vertices in `head`-local bind space (the palm measurement's sibling). */
export function measureBoneLocalPoints(skinned: THREE.SkinnedMesh, bone: THREE.Bone): THREE.Vector3[] {
  const index = skinned.skeleton.bones.indexOf(bone);
  const inverse = skinned.skeleton.boneInverses[index];
  const position = skinned.geometry.getAttribute('position');
  const skinIndex = skinned.geometry.getAttribute('skinIndex');
  const skinWeight = skinned.geometry.getAttribute('skinWeight');
  if (index < 0 || !inverse || !position || !skinIndex || !skinWeight) return [];
  const out: THREE.Vector3[] = [];
  for (let i = 0; i < position.count; i++) {
    let w = 0;
    for (let c = 0; c < 4; c++) if (skinIndex.getComponent(i, c) === index) w += skinWeight.getComponent(i, c);
    if (w > 0.5) out.push(new THREE.Vector3().fromBufferAttribute(position, i).applyMatrix4(inverse));
  }
  return out;
}

/**
 * Forehead band (reference rig units above the joint) and the reference body's
 * (char-4-rigged) half-width and front inside it. Rook measures crown 0.72,
 * width 0.79 and face depth 1.10 of the reference: a squat head, not a small
 * one. A uniform crown scale (the first cut) pulled every hat's front behind
 * the fringe (Janeks: "Red baseball cap on the Rook clips the hair too much in
 * the front"; the beanie and top hat did the same).
 */
const HEAD_BAND: readonly [number, number] = [0.12, 0.30];
export const HEAD_HALF_WIDTH_REF = 0.208;
export const HEAD_FRONT_REF = 0.186;
/**
 * Face probe for face-worn props (`faceAnchor`): rays straight back at the
 * cheeks beside the nose (|x| 0.03 and 0.045), over the rims' height
 * (±FACE_HALF_BAND, 5 mm steps). Hits further back than FACE_MIN_Z are the chin
 * or the neck, not the face. The low quartile reads the face plane: the nose,
 * and a fringe hanging in front of the face (Rook), only push hits forward.
 */
const FACE_RAY_X: readonly number[] = [0.03, -0.03, 0.045, -0.045];
const FACE_HALF_BAND = 0.03;
const FACE_MIN_Z = 0.08;
/**
 * Height of face-worn props per body, where the table row's height is wrong.
 * Eyes are texture, not geometry, so nothing on the mesh locates them: neither
 * the nose top nor its tip predicts both hand placements (Rook would land 2 cm
 * high). Keyed by the body's skinned-mesh node name (C1-C8, PlayerMesh,
 * Survivor1), reference rig units. Rook: Janeks's Blender placement, 2026-10-17.
 */
export const FACE_HEIGHT_BY_BODY: Readonly<Record<string, number>> = {
  C6: 0.1202,
};

/**
 * Per-axis scale (glTF axes: x width, y up, z depth) for a headgear prop about
 * the head joint. `points` are head-local (body-native units, +Z the face side)
 * and `boneWorldScale` converts them to metres, so `/ REFERENCE_RIG_SCALE` puts
 * them in the reference rig units the hats are authored in.
 *
 * y is the crown fit. A body whose crown fit is 1 gets (1, 1, 1), so the
 * standard heads keep exactly the fits verified on them. When it fires, width
 * follows the forehead band, and depth goes halfway from the crown fit to the
 * face depth: the back of such a head is a ponytail and cannot be measured.
 */
export function headgearFit(points: readonly THREE.Vector3[], boneWorldScale: number): THREE.Vector3 {
  const s = boneWorldScale / REFERENCE_RIG_SCALE;
  let crown = -Infinity;
  for (const p of points) if (p.z * s > -HEAD_BACK_CUTOFF && p.y * s > crown) crown = p.y * s;
  if (!Number.isFinite(crown) || crown <= 0) return new THREE.Vector3(1, 1, 1);
  const r = crown / HEAD_CROWN_REF;
  if (Math.abs(r - 1) < HEADGEAR_FIT_DEADBAND) return new THREE.Vector3(1, 1, 1);
  const fy = Math.min(HEADGEAR_FIT_MAX, Math.max(HEADGEAR_FIT_MIN, r));
  let halfW = -Infinity;
  let front = -Infinity;
  for (const p of points) {
    const y = p.y * s;
    if (y <= HEAD_BAND[0] || y >= HEAD_BAND[1]) continue;
    halfW = Math.max(halfW, Math.abs(p.x * s));
    front = Math.max(front, p.z * s);
  }
  if (!Number.isFinite(halfW)) return new THREE.Vector3(fy, fy, fy);
  const fx = Math.min(1, Math.max(fy, halfW / HEAD_HALF_WIDTH_REF));
  const fz = Math.min(1, Math.max(fy, (fy + front / HEAD_FRONT_REF) / 2));
  return new THREE.Vector3(fx, fy, fz);
}

/** A skinned mesh's whole surface in one bone's local bind space (body-native units). */
export interface BoneLocalMesh {
  positions: Float32Array;
  index: ArrayLike<number>;
}

/**
 * Every vertex of `skinned` moved into `bone`'s bind space, with the triangle
 * index. The whole mesh, not only head-weighted faces: a low-poly face can
 * straddle the neck weights and would drop out of the probe silently.
 */
export function measureBoneLocalMesh(skinned: THREE.SkinnedMesh, bone: THREE.Bone): BoneLocalMesh | null {
  const inverse = skinned.skeleton.boneInverses[skinned.skeleton.bones.indexOf(bone)];
  const position = skinned.geometry.getAttribute('position');
  if (!inverse || !position) return null;
  const positions = new Float32Array(position.count * 3);
  const v = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    v.fromBufferAttribute(position, i).applyMatrix4(inverse);
    positions[i * 3] = v.x;
    positions[i * 3 + 1] = v.y;
    positions[i * 3 + 2] = v.z;
  }
  const index = skinned.geometry.getIndex()?.array ?? Uint32Array.from({ length: position.count }, (_, i) => i);
  return { positions, index };
}

/** Largest z where the line (x, y, *) crosses the mesh, or null. Units of the mesh. */
function frontmostZ(mesh: BoneLocalMesh, x: number, y: number): number | null {
  const { positions: p, index } = mesh;
  let best = -Infinity;
  for (let t = 0; t + 2 < index.length; t += 3) {
    const a = index[t]! * 3, b = index[t + 1]! * 3, c = index[t + 2]! * 3;
    // Barycentric test in the xy plane, then interpolate z.
    const x0 = p[a]!, y0 = p[a + 1]!, x1 = p[b]!, y1 = p[b + 1]!, x2 = p[c]!, y2 = p[c + 1]!;
    const d = (y1 - y2) * (x0 - x2) + (x2 - x1) * (y0 - y2);
    if (d === 0) continue;
    const l0 = ((y1 - y2) * (x - x2) + (x2 - x1) * (y - y2)) / d;
    const l1 = ((y2 - y0) * (x - x2) + (x0 - x2) * (y - y2)) / d;
    const l2 = 1 - l0 - l1;
    if (l0 < 0 || l1 < 0 || l2 < 0) continue;
    best = Math.max(best, l0 * p[a + 2]! + l1 * p[b + 2]! + l2 * p[c + 2]!);
  }
  return Number.isFinite(best) ? best : null;
}

/**
 * The face plane at height `yCentre` (reference rig units), from the surface:
 * see FACE_RAY_X. Null when no ray lands on the face.
 */
export function faceSurface(mesh: BoneLocalMesh, boneWorldScale: number, yCentre: number): number | null {
  const s = boneWorldScale / REFERENCE_RIG_SCALE;
  const hits: number[] = [];
  for (let i = 0; i <= 12; i++) {
    const y = yCentre - FACE_HALF_BAND + i * 0.005;
    for (const x of FACE_RAY_X) {
      const z = frontmostZ(mesh, x / s, y / s);
      if (z !== null && z * s > FACE_MIN_Z) hits.push(z * s);
    }
  }
  if (hits.length === 0) return null;
  hits.sort((m, n) => m - n);
  return hits[Math.floor(hits.length / 4)]!;
}

/**
 * Where a face-worn prop's back sits: `gap` in front of the face plane (not
 * scaled: it is the air between rim and skin), with the prop's own `back`
 * depth (origin to rim back) scaled by the fit like the rest of it.
 */
export interface FaceAnchor {
  gap: number;
  back: number;
}

/**
 * The prop's offset from the head joint in reference rig units: the table row
 * with the per-axis fit applied. A face-worn prop takes its depth from this
 * body's face surface, and its height from FACE_HEIGHT_BY_BODY when listed.
 */
export function headgearOffset(
  position: readonly [number, number, number],
  fit: THREE.Vector3,
  face?: { anchor: FaceAnchor; mesh: BoneLocalMesh | null; boneWorldScale: number; height?: number },
): [number, number, number] {
  const out: [number, number, number] = [position[0] * fit.x, position[1] * fit.y, position[2] * fit.z];
  if (face) {
    if (face.height !== undefined) out[1] = face.height;
    const plane = face.mesh ? faceSurface(face.mesh, face.boneWorldScale, out[1]) : null;
    if (plane !== null) out[2] = plane + face.anchor.gap - face.anchor.back * fit.z;
  }
  return out;
}

export function fixedMetreOffset(
  position: readonly [number, number, number],
  bone: THREE.Bone,
): readonly [number, number, number] {
  const worldScale = new THREE.Vector3();
  bone.getWorldScale(worldScale);
  const factor = REFERENCE_RIG_SCALE / (worldScale.x || REFERENCE_RIG_SCALE);
  return [position[0] * factor, position[1] * factor, position[2] * factor];
}

// ─── Equipped-weapon -> visual/pose mapping ─────────────────────────────────

/**
 * Which authored arm-pose set an equipped weapon uses. `melee` used to be one
 * family covering every MELEE item; it is now split three ways because knife
 * and axe got their own one-handed holds — leaving `melee` as the name would
 * have been actively misleading, since knife and axe ARE melee. `blunt` is the
 * catch-all for two-handed makeshift weapons (plank, hockey stick) and matches
 * the clip files' own family spelling.
 */
type ArmsPoseFamily = 'unarmed' | 'blunt' | 'knife' | 'axe' | 'pistol' | 'rifle';
type ArmedPoseFamily = Exclude<ArmsPoseFamily, 'unarmed'>;

/**
 * Equipped item's stable content key (`ItemDefSummary.key`) -> held-weapon
 * GLB. No such mapping existed anywhere before this (`ItemDef` has no
 * `modelUrl` column) — built by cross-referencing `backend/prisma/seed.ts`'s
 * item catalog against the 8 shipped `/models/weapons/*.glb` files and
 * `asset_todo.md`'s own pose-pairing notes (`weapon_45cal.glb` = pistol
 * pose, `weapon_bullpup-classic.glb`/`weapon_boltaction-old.glb` = rifle
 * pose, `weapon_plank.glb` = melee pose). Pose family for FIREARM items
 * comes from `ItemDefSummary.ammoType` at call time (Open Question 3,
 * resolved below — every seeded firearm already has one), not hardcoded per
 * key, so this table only carries the visual.
 */
export const WEAPON_MODEL_BY_KEY: Readonly<Record<string, string>> = {
  rifle_bolt: `${WEAPON_DIR}/weapon_boltaction-old.glb`,
  carbine: `${WEAPON_DIR}/weapon_bullpup-classic.glb`,
  shotgun: `${WEAPON_DIR}/weapon_shotgun-classic.glb`,
  pistol: `${WEAPON_DIR}/weapon_45cal.glb`,
  // No distinct revolver model has shipped yet (only one pistol-shaped GLB
  // exists) — reuses weapon_45cal.glb. Flagged: visually approximate, not a
  // snub revolver, until a dedicated model exists.
  snub_revolver: `${WEAPON_DIR}/weapon_45cal.glb`,
  hunting_knife: `${WEAPON_DIR}/weapon_rusty-knife.glb`,
  axe: `${WEAPON_DIR}/weapon_rusty-axe.glb`,
  hockey_stick: `${WEAPON_DIR}/weapon_hockey-stick.glb`,
  // ── the full-catalog batch (2026-09-10, rig_all_weapons.py): every other
  // GLB under /models/weapons, keyed by filename slug. None of these has a
  // seeded ItemDef yet — they're reachable today via NPC `heldItemKey`, and a
  // future ItemDef should adopt the same key to get its visual for free. ──
  // rifles / SMGs / LMGs / shotguns
  ak_bullpup: `${WEAPON_DIR}/weapon_ak-bullpup.glb`,
  akm: `${WEAPON_DIR}/weapon_akm.glb`,
  auto_shotgun: `${WEAPON_DIR}/weapon_auto-shotgun.glb`,
  fn2000: `${WEAPON_DIR}/weapon_fn2000.glb`,
  frf2: `${WEAPON_DIR}/weapon_frf2.glb`,
  garand: `${WEAPON_DIR}/weapon_garand.glb`,
  m16: `${WEAPON_DIR}/weapon_m16.glb`,
  m249: `${WEAPON_DIR}/weapon_m249.glb`,
  m82: `${WEAPON_DIR}/weapon_m82.glb`,
  mp4: `${WEAPON_DIR}/weapon_mp4.glb`,
  mp5: `${WEAPON_DIR}/weapon_mp5.glb`,
  mp7: `${WEAPON_DIR}/weapon_mp7.glb`,
  p90: `${WEAPON_DIR}/weapon_p90.glb`,
  pkm: `${WEAPON_DIR}/weapon_pkm.glb`,
  ppsh: `${WEAPON_DIR}/weapon_ppsh.glb`,
  sawed_off: `${WEAPON_DIR}/weapon_sawed-off.glb`,
  scar: `${WEAPON_DIR}/weapon_scar.glb`,
  scar_alt: `${WEAPON_DIR}/weapon_scar-alt.glb`,
  scar_scope: `${WEAPON_DIR}/weapon_scar-scope.glb`,
  svd: `${WEAPON_DIR}/weapon_svd.glb`,
  ump45: `${WEAPON_DIR}/weapon_ump45.glb`,
  // New catalog entries added 2026-09-15 (Tripo-generated).
  vss: `${WEAPON_DIR}/weapon_vss.glb`,
  g36: `${WEAPON_DIR}/weapon_g36.glb`,
  // pistols
  mac10: `${WEAPON_DIR}/weapon_mac10.glb`,
  magnum: `${WEAPON_DIR}/weapon_magnum.glb`,
  // melee / thrown
  bat: `${WEAPON_DIR}/weapon_bat.glb`,
  crowbar: `${WEAPON_DIR}/weapon_crowbar.glb`,
  dynamite: `${WEAPON_DIR}/weapon_dynamite.glb`,
  flail: `${WEAPON_DIR}/weapon_flail.glb`,
  machete: `${WEAPON_DIR}/weapon_machete.glb`,
  maul: `${WEAPON_DIR}/weapon_maul.glb`,
  // `plank` the ITEM is a MATERIAL and never player-equippable, but the model
  // is now NPC-holdable like everything else (heldItemKey: 'plank').
  plank: `${WEAPON_DIR}/weapon_plank.glb`,
  sledgehammer: `${WEAPON_DIR}/weapon_sledgehammer.glb`,
  // Premium weapons (2026-10-19, `premium_weapons_build.py`). New files only:
  // the retextures are copies of their source GLB with a re-baked albedo, so
  // `weapon_45cal.glb` and the rest stay exactly as shipped. Visual only until
  // their ItemDefs are seeded (stats are Janeks's next call).
  fire_axe_red: `${WEAPON_DIR}/weapon_fire-axe-red.glb`,
  fire_axe_hazard: `${WEAPON_DIR}/weapon_fire-axe-hazard.glb`,
  fire_axe_forged: `${WEAPON_DIR}/weapon_fire-axe-forged.glb`,
  sword_spring: `${WEAPON_DIR}/weapon_sword-spring.glb`,
  sword_blued: `${WEAPON_DIR}/weapon_sword-blued.glb`,
  pistol_nickel: `${WEAPON_DIR}/weapon_45cal-nickel.glb`,
  deagle: `${WEAPON_DIR}/weapon_deagle.glb`,
  mp5_urban: `${WEAPON_DIR}/weapon_mp5-urban.glb`,
  tommygun: `${WEAPON_DIR}/weapon_tommygun.glb`,
  akm_raider: `${WEAPON_DIR}/weapon_akm-raider.glb`,
  scar_woodland: `${WEAPON_DIR}/weapon_scar-woodland.glb`,
  m16_rattlecan: `${WEAPON_DIR}/weapon_m16-rattlecan.glb`,
  pkm_arctic: `${WEAPON_DIR}/weapon_pkm-arctic.glb`,
};

/**
 * Per-weapon grip position/rotation, applied to the weapon root after
 * parenting to `hand.R`. Keyed by `ItemDefSummary.key`.
 *
 * Derived in Blender against the real arm poses and the real weapon geometry
 * — see `assets/WEAPON_GRIP_TABLE.md` for the method and the per-weapon
 * rationale. Two things about these numbers that are NOT obvious:
 *
 *  - **`rotation` is a three.js `XYZ` Euler, which is not what Blender's
 *    `to_euler()` hands you.** Blender's `XYZ` composes `Rz·Ry·Rx`; three.js's
 *    composes `Rx·Ry·Rz`. The values below come from Blender's
 *    `to_euler('ZYX')` for that reason. The naive read-out reconstructs the
 *    intended matrix with a max error of ~1.72; these reconstruct to 3.6e-07.
 *    **If you ever regenerate these, do not take the obvious read-out.**
 *  - **Real-world weapon size lives in the GLBs, not here.** All 8 weapon
 *    models were Tripo-normalised to a 1.0-unit longest axis (the pistol was
 *    literally a metre long); each now carries its true length as a root-node
 *    scale, so `attachToBone`'s rig-scale cancel leaves them at correct
 *    metres. There is deliberately no scale field in this table. (The small
 *    weapons are intentionally a little oversized — a true-scale 0.22 m pistol
 *    is shorter than this rig's own hand mesh and reads as a toy.)
 *  - **`position` is in bone-local RIG units, not metres — and the weapon's
 *    geometry beside it is in metres.** The offset is a child transform of
 *    `hand.R`, so the bone's world scale (1.8035 on `char-4-rigged`) multiplies
 *    it, while `attachToBone` divides that same scale back out of the geometry.
 *    The two are genuinely in different units, by construction of this runtime
 *    rather than by choice. **Multiply a position by the body's own
 *    `1.8 / bboxHeight` to reason about it in metres.** Do NOT "correct" the
 *    position by dividing it the way the geometry is divided — that was the
 *    2026-09-16 `scaledGripPosition` bug, which is what "the weapons are not
 *    properly attached to the hands" was; see `attachToBone`'s doc comment.
 *    The anchor these are solved against is the centroid of the vertices
 *    actually weighted to `hand.R` (~69% along the bone and off-axis), not the
 *    bone head — anchoring to the head buries the weapon in the sleeve.
 *  - **Weapon size is read off the shipped GLB, never from a table.** 31 of the
 *    40 shipped weapons had drifted from `scale_weapon_glbs.SIZES` by 2026-09-18
 *    (hand re-scales in Blender that never went back into the table — akm by
 *    12%, m82 by 35%), and the solver was using SIZES. It now reads the root
 *    node's own `scale`, which is the same number `attachToBone` reads.
 *
 *  - **A rotation must be derived in glTF space, never in the .blend's object
 *    axes.** The first shipped table was wrong for all 8 weapons this exact
 *    way: the rotations were expressed in the axes of `weapons-grip.blend`'s
 *    `W_*` objects, several of which had been re-oriented while the grips were
 *    authored. This runtime parents the **raw GLB** to the bone, so every
 *    weapon rendered off by precisely its own .blend re-orientation — the
 *    bolt-action's raw long axis is glTF Y, so it hung vertically beside the
 *    hand. The corrected rows come from `scripts/derive_grips_gltf.py`, which
 *    composes the posed `hand.R` from the shipped GLB nodes plus the shipped
 *    `idle-armed-*-arms` keyframes — i.e. reproduces this module's own math
 *    rather than Blender's. A correct position with a wrong rotation swings the
 *    handle out of the palm, which is why that bug read as displacement.
 *  - **This table cannot be verified in Blender, and must not be TUNED there
 *    either.** Every row here is now solver output: `derive_grips_gltf.py`
 *    reproduces all 41 byte-for-byte, and `weapon-grip.test.ts` fails if one
 *    stops matching. A row posed by hand in Blender looks right in Blender and
 *    ships wrong — that happened twice (2026-09-10 rotations in `.blend` object
 *    axes; 2026-09-15 positions against a `hand.R` resize that never reached
 *    `char-4-rigged.glb`, so 12 rows were tuned against a bone no shipped file
 *    has). **Change the intent in `weapon_grip.py` and re-run the solver.**
 *    The non-circular eyeball check is three.js, not Blender: equip one and
 *    open the paperdoll (`ui/character-preview.ts` renders the player animator
 *    front-on and lit, which is the view the 55° play camera cannot give).
 *
 * `plank` has a row despite being a MATERIAL ItemDef (never player-equippable)
 * because NPC `heldItemKey` can hold any catalog weapon since 2026-09-10.
 */
export const WEAPON_GRIP_OFFSET: Readonly<Record<string, {
  position?: readonly [number, number, number];
  /** Euler XYZ, radians — see the note above on Euler order. */
  rotation?: readonly [number, number, number];
}>> = {
  // Shares weapon_45cal.glb with snub_revolver, hence the identical row.
  // The pistol ships deliberately oversized (0.237x of a 1.0-unit Tripo
  // normalise ~= 0.24 m): a true-scale 0.22 m pistol is shorter than this
  // rig's own hand mesh and reads as a toy.
  pistol:        { position: [ 0.02025,  0.07072,  0.09157], rotation: [ 0.90376, -0.44276,  1.29451] },
  snub_revolver: { position: [ 0.02025,  0.07072,  0.09157], rotation: [ 0.90376, -0.44276,  1.29451] },
  rifle_bolt:    { position: [ 0.05835,  0.13216, -0.04844], rotation: [ 0.73323, -0.26953,  1.58095] },
  carbine:       { position: [ 0.02570,  0.06863,  0.03766], rotation: [ 0.74611, -0.55905,  1.59030] },
  shotgun:       { position: [ 0.21473,  0.21513, -0.15217], rotation: [-2.42131, -0.11389,  1.56377] },
  // Blade-UP is deliberate: `FAMILY_CARRY.blunt` carries a stick angled
  // up-forward off the shoulder, not dragged like a cane. It reads as "held
  // ready to swing", which looks odd enough at a glance to invite a "fix".
  // It is not one.
  hockey_stick:  { position: [-0.59494, -0.30131,  0.30827], rotation: [-1.20460, -0.68045, -1.47898] },
  axe:           { position: [-0.14589, -0.08793, -0.05745], rotation: [ 1.29610, -2.27169, -0.46066] },
  hunting_knife: { position: [-0.10009,  0.02361, -0.00079], rotation: [ 0.82250, -3.01246, -0.34856] },
  // ── rifles / SMGs / LMGs / shotguns: across-chest low-ready ──
  ak_bullpup:    { position: [ 0.10908,  0.11527, -0.05326], rotation: [ 2.29318,  0.00778,  1.96265] },
  akm:           { position: [ 0.09349,  0.13350, -0.05167], rotation: [-0.85089, -0.00678,  1.20557] },
  auto_shotgun:  { position: [ 0.01695, -0.00695,  0.08819], rotation: [-0.83354, -0.01777,  0.99899] },
  fn2000:        { position: [ 0.07104,  0.00171,  0.07869], rotation: [ 2.30569,  0.01398,  2.02280] },
  frf2:          { position: [ 0.02055, -0.04149,  0.13814], rotation: [ 2.25183,  0.00315,  1.66882] },
  garand:        { position: [ 0.06271, -0.04470,  0.14093], rotation: [ 0.72097,  0.09643,  1.57776] },
  m16:           { position: [ 0.10654,  0.13089, -0.05719], rotation: [ 2.29007,  0.00661,  1.88344] },
  m249:          { position: [ 0.13896,  0.03484,  0.04844], rotation: [ 0.72236, -0.26335,  1.57805] },
  m82:           { position: [ 0.12343,  0.18707, -0.12133], rotation: [-0.85462, -0.00602,  1.34385] },
  mp4:           { position: [ 0.10658,  0.10792, -0.04368], rotation: [ 2.29215,  0.00735,  1.95469] },
  mp5:           { position: [ 0.17723,  0.04077,  0.00287], rotation: [ 2.29121,  0.00699,  1.85875] },
  mp7:           { position: [ 0.02487,  0.04107,  0.01736], rotation: [-0.83879,  3.12147, -0.71812] },
  p90:           { position: [ 0.05137,  0.14485, -0.06840], rotation: [ 2.29716,  0.00807,  1.74783] },
  pkm:           { position: [ 0.07921,  0.10274, -0.05026], rotation: [ 0.67275, -0.29757,  1.56350] },
  ppsh:          { position: [ 0.02603,  0.10224, -0.01279], rotation: [ 2.19588, -0.00666,  1.71313] },
  sawed_off:     { position: [ 0.02199,  0.09291, -0.02590], rotation: [-2.41807,  0.31779,  1.56319] },
  scar:          { position: [ 0.11184,  0.12746, -0.04797], rotation: [ 2.29140,  0.00704,  1.90117] },
  scar_alt:      { position: [ 0.12752,  0.09890, -0.01891], rotation: [ 2.29291,  0.00760,  1.92173] },
  scar_scope:    { position: [ 0.10722,  0.11660, -0.03646], rotation: [ 2.29859,  0.00929,  1.87394] },
  svd:           { position: [ 0.07542,  0.14411, -0.08077], rotation: [ 2.28424,  0.00553,  1.77804] },
  ump45:         { position: [ 0.11897,  0.16231, -0.07540], rotation: [ 2.29043,  0.00680,  1.81463] },
  vss:           { position: [ 0.03901,  0.09097, -0.01346], rotation: [ 2.29140,  0.00704,  1.86032] },
  g36:           { position: [ 0.04297,  0.09048, -0.01545], rotation: [ 0.72924, -0.26652,  1.57986] },
  // ── pistol-family: compact two-hand low hold ──
  mac10:         { position: [ 0.04705,  0.05847,  0.10119], rotation: [ 0.91505, -0.44534,  1.34187] },
  magnum:        { position: [ 0.06086,  0.00130,  0.03713], rotation: [-2.24145,  0.44200,  1.82683] },
  // ── melee / thrown ──
  // These seven had no entry in `weapon_grip.WEAPONS` until 2026-09-18, so
  // the solver could never run for them and their rows were posed by hand in
  // Blender. They now derive like everything else — see that file's melee
  // block for how each haft/head band was chosen (mean radius around the long
  // axis, NOT vertex density, which calls a sledgehammer's HEAD the grip).
  bat:           { position: [-0.27802,  0.14156,  0.20366], rotation: [-0.35079,  1.12619, -3.13521] },
  crowbar:       { position: [-0.16172, -0.05595,  0.15404], rotation: [-0.53951,  0.21387, -2.29343] },
  dynamite:      { position: [-0.05065,  0.05846,  0.03867], rotation: [-0.77836,  0.16025, -3.03526] },
  flail:         { position: [-0.21039, -0.03614,  0.21306], rotation: [-0.34725,  0.67993, -2.57798] },
  machete:       { position: [-0.22201,  0.10833,  0.11717], rotation: [-0.81639,  0.48133, -3.02311] },
  maul:          { position: [-0.32026, -0.08950,  0.31477], rotation: [-0.33235,  0.66126, -2.58724] },
  // `plank` is a seeded MATERIAL ItemDef, never player-equippable — it has a
  // row because an NPC's `heldItemKey` can name any catalog weapon, and
  // because `blunt` is the pose FAMILY's own file name.
  plank:         { position: [-0.24893, -0.14626,  0.01768], rotation: [ 1.28821, -3.06183, -0.08807] },
  sledgehammer:  { position: [-0.25265, -0.05681,  0.25689], rotation: [-0.34725,  0.67993, -2.57798] },
  // Premium weapons (2026-10-19) — solver output, `derive_grips_gltf.py`. A
  // retexture's row equals its source's: same vertices, same order.
  fire_axe_red:  { position: [-0.16761, -0.04891,  0.13506], rotation: [ 1.27719, -2.30230,  0.37813] },
  fire_axe_hazard: { position: [-0.16761, -0.04891,  0.13506], rotation: [ 1.27719, -2.30230,  0.37813] },
  fire_axe_forged: { position: [-0.16761, -0.04891,  0.13506], rotation: [ 1.27719, -2.30230,  0.37813] },
  sword_spring:  { position: [-0.23557,  0.11243,  0.12354], rotation: [-0.81829,  0.49540, -3.02222] },
  sword_blued:   { position: [-0.23557,  0.11243,  0.12354], rotation: [-0.81829,  0.49540, -3.02222] },
  pistol_nickel: { position: [ 0.02025,  0.07072,  0.09157], rotation: [ 0.90376, -0.44276,  1.29451] },
  deagle:        { position: [-0.07563,  0.14666,  0.11683], rotation: [-1.72901, -1.07320, -1.11907] },
  mp5_urban:     { position: [ 0.17723,  0.04077,  0.00287], rotation: [ 2.29121,  0.00699,  1.85875] },
  tommygun:      { position: [-0.04609,  0.12033, -0.05090], rotation: [-2.43810, -1.18018, -0.01848] },
  akm_raider:    { position: [ 0.13706,  0.17283, -0.08657], rotation: [-0.85637, -0.00474,  1.21323] },
  scar_woodland: { position: [ 0.11184,  0.12746, -0.04797], rotation: [ 2.29140,  0.00704,  1.90117] },
  m16_rattlecan: { position: [ 0.10654,  0.13089, -0.05719], rotation: [ 2.29007,  0.00661,  1.88344] },
  pkm_arctic:    { position: [ 0.07921,  0.10274, -0.05026], rotation: [ 0.67275, -0.29757,  1.56350] },
};

// ─── Equipment visuals (HEADGEAR, ...) — rigid single-bone attach ───────────
//
// Same mechanism as weapons: a standalone, skinless GLB parented to one bone
// via `attachToBone`, with a fixed bone-local offset. Only HEADGEAR is wired
// so far (checklist.md's 18-item equipment/clothing request) — GLOVES/FEET/
// TORSO/LEGS need either a second bone (feet, symmetric L/R) or true
// multi-bone skinning (soft garments that must deform through a walk/crouch
// cycle), neither of which this table or `attachToBone` covers. Don't
// repurpose HEADGEAR's single-bone path for something that needs to move at
// two bones — add a parallel table + bone field instead, the way `handBone`/
// `headBone` are already separate.
//
// Offsets are derived the SAME way `derive_grips_gltf.py` derives weapon
// grips: pure glTF-space math against the real shipped char-4-rigged.glb (see
// `assets/blender/scripts/verify_cap_offset.py`), NOT Blender's internal
// bone-local matrix — the first attempt at `cap_baseball` baked Blender
// Z-up "head-bone-local" coordinates directly into the mesh and then let
// Blender's own Y-up export convert them AGAIN, corrupting the result. Positions
// only, no rotation, needed so far — headgear doesn't need to point anywhere
// the way a weapon does, so there's no Euler-order pitfall to repeat.
export const EQUIPMENT_MODEL_BY_KEY: Readonly<Record<string, string>> = {
  cap_baseball: `${ITEMS_DIR}/item_cap-baseball.glb`,
  // The headgear batch (`assets/blender/scripts/hat_build.py`). Unlike the cap
  // above, these carry REFERENCE_RIG_SCALE on their root node: `attachToBone`
  // renders a prop's geometry in metres, and they are modelled in char-4 rig
  // units. Offsets are the same `b2g(CENTER_W) - W_head`, printed by the build;
  // fit rendered by `verify_hat_fit.py` on several bodies.
  hat_party: `${ITEMS_DIR}/item_hat_party.glb`,
  cap_baseball_red: `${ITEMS_DIR}/item_cap_baseball_red.glb`,
  cap_baseball_multicam: `${ITEMS_DIR}/item_cap_baseball_multicam.glb`,
  helmet_army: `${ITEMS_DIR}/item_helmet_army.glb`,
  helmet_tactical: `${ITEMS_DIR}/item_helmet_tactical.glb`,
  helmet_visor_gold: `${ITEMS_DIR}/item_helmet_visor_gold.glb`,
  mask_gas: `${ITEMS_DIR}/item_mask_gas.glb`,
  mask_hockey: `${ITEMS_DIR}/item_mask_hockey.glb`,
  hat_top: `${ITEMS_DIR}/item_hat_top.glb`,
  // Its propeller is a separate child node named `Propeller` (spins about its
  // own Y) in case it is ever animated; nothing spins it today.
  beanie_propeller: `${ITEMS_DIR}/item_beanie_propeller.glb`,
  // Headsets and glasses (same builder). They take the one HEADGEAR slot, so a
  // headset replaces a helmet rather than stacking with it.
  headset_comms_od: `${ITEMS_DIR}/item_headset_comms_od.glb`,
  headset_comms_coyote: `${ITEMS_DIR}/item_headset_comms_coyote.glb`,
  headset_ops_black: `${ITEMS_DIR}/item_headset_ops_black.glb`,
  headset_earpro_neckband: `${ITEMS_DIR}/item_headset_earpro_neckband.glb`,
  headset_range_muffs: `${ITEMS_DIR}/item_headset_range_muffs.glb`,
  glasses_nerd: `${ITEMS_DIR}/item_glasses_nerd.glb`,
};

export const EQUIPMENT_GRIP_OFFSET: Readonly<Record<string, {
  position?: readonly [number, number, number];
  rotation?: readonly [number, number, number];
  /**
   * Face-worn headgear only: depth from the body's measured face surface,
   * replacing `position[2]` (see `headgearOffset`). Faces sit 0.14-0.20 in
   * front of the joint across the bodies, so one fixed depth either floats
   * glasses off flat faces or sinks them into long ones.
   */
  faceAnchor?: FaceAnchor;
}>> = {
  cap_baseball: { position: [0.0, 0.385, 0.01] },
  hat_party: { position: [0.0, 0.397, 0.0321] },
  cap_baseball_red: { position: [0.0, 0.4058, 0.0775] },
  cap_baseball_multicam: { position: [0.0, 0.4058, 0.0775] },
  helmet_army: { position: [0.0, 0.2945, -0.007] },
  helmet_tactical: { position: [0.0, 0.295, 0.0315] },
  helmet_visor_gold: { position: [0.0, 0.3229, -0.0113] },
  mask_gas: { position: [-0.0083, 0.1893, 0.0661] },
  mask_hockey: { position: [0.0, 0.2375, 0.019] },
  hat_top: { position: [0.0, 0.555, -0.009] },
  beanie_propeller: { position: [0.0, 0.4475, -0.0055] },
  headset_comms_od: { position: [0.0031, 0.2675, 0.0985] },
  headset_comms_coyote: { position: [0.0031, 0.2416, 0.0964] },
  headset_ops_black: { position: [0.0025, 0.2675, 0.1015] },
  headset_earpro_neckband: { position: [0.0, 0.2, -0.124] },
  headset_range_muffs: { position: [0.0, 0.291, -0.01] },
  // Face-worn: depth from each body's face surface (`faceAnchor`). Height and
  // gap are Janeks's npc-survivor-1 placement, 2026-10-17 ("right on the
  // nose"); the gap reproduces his Rook placement within 4 mm. `back` is the
  // frame's rim back, 0.111 in front of its origin.
  glasses_nerd: { position: [0.0, 0.1946, 0.0611], faceAnchor: { gap: 0.0136, back: 0.111 } },
};

// ─── Equipment visuals (TORSO) — rigid single-bone attach at `chest` ────────
//
// The "true multi-bone skinning" case the comment above flags as unbuilt
// turns out not to apply to a plate-carrier vest: `chest`, `spine`, and
// `shoulder.L/R` carry ZERO animated translation/rotation across every
// shipped clip (idle/walk/run/crouch-idle/crouch-move, both LEGS and ARMS
// files — verified by parsing each clip's sampler output span before
// building `item_vest-light.glb`). A vest rigidly parented to `chest`
// inherits the whole hips->spine->chest chain (including the small hip
// bounce in crouch-move) automatically and never needs to deform on its
// own, so this reuses the exact HEADGEAR pattern — same `attachToBone`, a
// parallel table, one bone field — rather than inventing a skin-rebind
// path nothing here needs yet.
//
// Offset derived the same way as `cap_baseball` above (see
// `assets/blender/scripts/vest_build.py`'s docstring): the vest mesh is
// self-centered at its own local origin, and
// `position = b2g(CENTER_W) - W_chest.translation`, where `W_chest`
// (rest pose, glTF space) is (0, 0.4, 0.01) — read directly off
// `char-4-rigged.glb`'s node TRS, exact (not approximated) because
// `chest`'s own `rotation` field, and every ancestor's, is absent
// (identity) in that file.
export const TORSO_MODEL_BY_KEY: Readonly<Record<string, string>> = {
  vest_light: `${ITEMS_DIR}/item_vest-light.glb`,
  // `plate_vest`, not a new `vest_heavy` key. asset_todo.md's clothing batch
  // asks for a "heavier plate-carrier sibling to vest_light" — but `plate_vest`
  // (HEAVY, 30 armour, STR 4, -0.15 runSpeed) was already seeded, already in
  // three loot tables and a vendor catalog, and had no model. Adding a second
  // HEAVY TORSO vest would have duplicated it mechanically and left this one
  // invisible; the art is the only piece that was actually missing.
  plate_vest: `${ITEMS_DIR}/item_vest-heavy.glb`,
};

export const TORSO_GRIP_OFFSET: Readonly<Record<string, {
  position?: readonly [number, number, number];
  rotation?: readonly [number, number, number];
}>> = {
  // Re-tuned 2026-09-15 in Blender after the flip/thinning pass — position
  // moved and a (0.7087, 0.9175, 1.0) non-uniform scale baked into
  // item_vest-light.glb instead of living here (this table has no scale
  // field, same convention as WEAPON_GRIP_OFFSET). Position re-tuned again
  // same day, read live off VestGrip_chest (local transform, rotation left
  // at identity by the user this time — not zeroed by us).
  vest_light: { position: [0.0, 0.04443, -0.00904] },
  // Derived, not tuned by eye: `item_vest-heavy.glb` is authored in absolute
  // character coordinates and centred on CENTER_W = (0, -0.01, 0.36) Blender /
  // (0, 0.36, 0.01) glTF at export, so position = b2g(CENTER_W) - W_chest =
  // (0, 0.36-0.40, 0.01-0.01). See `assets/blender/scripts/vest_heavy_build.py`
  // — and `verify_torso_render.py`, which renders this exact offset onto the
  // real char-4-rigged.glb at rest AND at the arm-swing extremes.
  plate_vest: { position: [0.0, -0.04, 0.0] },
};

/**
 * Stationary armed hold, per family.
 *
 * Open Question 4 (character-animation-runtime.md §5) — confirmed:
 * `plank` is a seeded `MATERIAL` `ItemDef` (a crafting resource), not any
 * equippable weapon's key. `weapon_plank.glb` / `idle-armed-plank(-arms)` are
 * pose-FAMILY names only ("generic two-handed makeshift weapon"), which is
 * why the `blunt` family maps to the `-plank-` file rather than a `-blunt-`
 * one. Knife and axe were later given their own one-handed holds, so they no
 * longer share it.
 */
export const IDLE_ARMED_CLIP: Record<ArmedPoseFamily, ArmsClipName> = {
  blunt: 'idle-armed-plank-arms',
  knife: 'idle-armed-knife-arms',
  axe: 'idle-armed-axe-arms',
  pistol: 'idle-armed-pistol-arms',
  rifle: 'idle-armed-rifle-arms',
};

/**
 * `hit` one-shot reaction, per pose family (`unarmed` included — the only
 * table in this file that needs it, since a hit-react has to work whether
 * or not the character is holding anything). Only `chest`/`neck`/`head`
 * animate in these clips; `upper_arm`/`forearm`/`hand` are held at the
 * family's existing constant grip pose for the whole clip, so a weapon
 * parented to `hand.R` is carried backward through FK along with the rest
 * of the arm instead of being re-posed — see author_hit_react.py's doc
 * comment for why the grip can't be touched here.
 */
const HIT_REACT_ARMS_CLIP: Record<ArmsPoseFamily, ArmsClipName> = {
  unarmed: 'hit-arms-unarmed',
  pistol: 'hit-arms-pistol',
  rifle: 'hit-arms-rifle',
  knife: 'hit-arms-knife',
  axe: 'hit-arms-axe',
  blunt: 'hit-arms-blunt',
};

/**
 * `attack` one-shot, per pose family: firearm recoil for pistol/rifle, a
 * windup-strike-follow-through swing for the three melee families.
 *
 * PARTIAL, unlike `HIT_REACT_ARMS_CLIP` — `unarmed` is deliberately absent.
 * There is no unarmed attack asset (the brief was rifle/pistol/melee), so
 * Bare hands get their own clip (`attack-arms-unarmed`, added 2026-09-19), so
 * `playAttack()` no longer no-ops for them. It still refuses to fall back to
 * a MELEE swing for an unlisted family — that would read as swinging a weapon
 * the character does not have, which is the failure the Partial record
 * protects against.
 *
 * Unlike hit-react, these clips DO move `upper_arm`/`forearm`/`hand` — a swing
 * is the arm chain moving, and the grip-untouched assertion that protects
 * hit-react is intentionally not applied to them. What protects the grip here
 * instead is that every clip starts and ends on the exact pose its weapon grip
 * was solved against, asserted in author_attack_clips.py.
 *
 * CAVEAT on `rifle`: it is built on the AIM pose, because the rifle's
 * low-ready carry aims the barrel down-left at ~-32 deg (weapon_grip.py's
 * `FAMILY_CARRY['rifle']`) and firing from it would shoot the ground. It is
 * therefore only muzzle-correct while the NPC `aim` activity's grip
 * (`AIM_GRIP_OFFSET`) is applied. Closed for the player on 2026-09-19: the
 * player's aim branch now plays `AIM_ARMS_CLIP` with the matching grip, so
 * the recoil starts from the pose it was authored against. A gap remains for
 * not something this table can fix.
 */
const ATTACK_ARMS_CLIP: Partial<Record<ArmsPoseFamily, ArmsClipName>> = {
  // Two punches, not one: the request was a fist FIGHT, and a single jab over
  // a 24-frame clip reads as a poke. The arms slot is owned for the whole
  // clip either way, so the second punch costs nothing.
  unarmed: 'attack-arms-unarmed',
  pistol: 'attack-arms-pistol',
  rifle: 'attack-arms-rifle',
  knife: 'attack-arms-knife',
  axe: 'attack-arms-axe',
  blunt: 'attack-arms-blunt',
};

/**
 * Locomotion arms, per family — closes the gap asset_todo.md §7C flagged and
 * this module previously worked around by holding a STATIC armed idle pose
 * while the legs ran (documented at the time as the cheaper of the two
 * options). Every clip's duration matches its legs partner exactly, so the
 * two slots stay phase-locked without re-timing.
 *
 * `unarmed` keeps the older `-arms-unarmed` file naming; the five armed
 * families use `-arms-<family>`.
 */
const LOCOMOTION_ARMS_CLIP: Record<ArmsPoseFamily, { walk: ArmsClipName; run: ArmsClipName; crouchMove: ArmsClipName }> = {
  unarmed: { walk: 'walk-arms-unarmed', run: 'run-arms-unarmed', crouchMove: 'crouch-move-arms-unarmed' },
  pistol: { walk: 'walk-arms-pistol', run: 'run-arms-pistol', crouchMove: 'crouch-move-arms-pistol' },
  rifle: { walk: 'walk-arms-rifle', run: 'run-arms-rifle', crouchMove: 'crouch-move-arms-rifle' },
  blunt: { walk: 'walk-arms-blunt', run: 'run-arms-blunt', crouchMove: 'crouch-move-arms-blunt' },
  knife: { walk: 'walk-arms-knife', run: 'run-arms-knife', crouchMove: 'crouch-move-arms-knife' },
  axe: { walk: 'walk-arms-axe', run: 'run-arms-axe', crouchMove: 'crouch-move-arms-axe' },
};

/**
 * MELEE item key -> its own pose family. Anything not listed falls back to
 * `blunt`, the two-handed makeshift hold — so an unlisted new melee item gets
 * a sane pose rather than none, and only weapons with bespoke art need an
 * entry. Keyed on the item key rather than `ItemDef.kind` because `kind` is
 * `MELEE` for all of them and cannot distinguish a knife from a plank.
 */
const MELEE_FAMILY_BY_KEY: Readonly<Record<string, ArmedPoseFamily>> = {
  hunting_knife: 'knife',
  axe: 'axe',
  // full-catalog batch (2026-09-10) — only the non-blunt melee need rows,
  // since `blunt` is the fallback for anything unlisted.
  machete: 'knife',
  dynamite: 'knife',
  crowbar: 'axe',
  // Premium melee (2026-10-19): fire axes take the axe hold, swords the machete's.
  fire_axe_red: 'axe',
  fire_axe_hazard: 'axe',
  fire_axe_forged: 'axe',
  sword_spring: 'knife',
  sword_blued: 'knife',
};

/**
 * Item -> the arms pose family it is held in.
 *
 * ─── The table is the authority, and this is a fix ──────────────────────────
 * This used to derive a FIREARM's family from `ammoType`: `'pistol'` ammo ->
 * the pistol hold, anything else -> the rifle hold. That reads like "derived
 * from real data instead of a guessed default" (its old comment said exactly
 * that) and it is wrong, because **ammoType is about ammunition, not about
 * how a gun is held.** Every `smg`-class weapon in the catalog is
 * `ammoType: 'pistol'` (`backend/prisma/content/weapons.ts`) — an MP5 fires a
 * pistol cartridge and is still held with two hands on a handguard.
 *
 * The consequence, reported by Janeks on 2026-09-19 as *"one handed guns
 * (pistols, mp5 and others) have both hands raised"*: `mp4`, `mp5`, `mp7`,
 * `p90`, `ppsh` and `ump45` played the PISTOL clip **for the player only**.
 * NPCs were fine — they read `WEAPON_POSE_FAMILY_BY_KEY`, which calls all six
 * `rifle`.
 *
 * And it was worse than a wrong pose. A grip offset is solved against ONE
 * pose (`weapon_grip.WEAPONS[stem]['family']` picks which), and all six are
 * solved as `rifle`. So the player got a rifle grip replayed on a pistol
 * hold — the weapon out of the hand, which is the same class of defect as a
 * mis-specced handle and looks identical.
 *
 * Reading the shared table first removes the disagreement at the source
 * rather than patching `ammoType`'s output. The table covers all 41 keys in
 * `WEAPON_MODEL_BY_KEY` exactly, so the fallbacks below are only reached by
 * an item with no weapon model at all.
 *
 * Open Question 5 — TOOL-kind items fall back to unarmed; no TOOL `ItemDef`
 * is seeded yet, so that branch is currently unreachable in practice.
 */
function armsPoseFamilyFor(primary: EquippedItemSummary | undefined): ArmsPoseFamily {
  if (!primary) return 'unarmed';
  const { kind, key, ammoType } = primary.def;
  // One source of truth, shared with the NPC path and mirrored by the grip
  // solver. See `armed-pose.test.ts`, which asserts all three agree.
  const declared = WEAPON_POSE_FAMILY_BY_KEY[key];
  if (declared) return declared;
  if (kind === 'MELEE') return MELEE_FAMILY_BY_KEY[key] ?? 'blunt';
  // `'shotgun'` ammoType deliberately lands on the rifle hold — no distinct
  // shotgun pose was authored, and a shotgun is held the same two-handed way.
  if (kind === 'FIREARM') return ammoType === 'pistol' ? 'pistol' : 'rifle';
  return 'unarmed';
}

/**
 * `armsPoseFamilyFor` by item KEY, for tests.
 *
 * The real function takes an `EquippedItemSummary` so it can fall back to
 * `kind`/`ammoType` for an item with no weapon model. Every key in
 * `WEAPON_MODEL_BY_KEY` resolves off the shared table before those fallbacks
 * are reached, so a key is all a test needs — and building a fake ItemDef in
 * the test would let the test disagree with the seed about `ammoType`, which
 * is the very thing being guarded.
 */
export function armsPoseFamilyForTest(key: string): ArmsPoseFamily {
  return armsPoseFamilyFor({ def: { key, kind: 'FIREARM' } } as unknown as EquippedItemSummary);
}

// ─── Playback tuning ─────────────────────────────────────────────────────────

/** Steady-state <-> steady-state (idle/walk/run/crouch-idle/crouch-move swaps), both slots. `warp: true` — differing-length loops (e.g. walk<->run) re-time smoothly instead of visually stuttering. */
const CROSSFADE_SECONDS = 0.25;
/** Into/out of the one-shot `stand-to-crouch-legs` transition. `warp: false` — warping a short 20-frame clip against a 32-48 frame locomotion cycle would time-distort it rather than blend it. */
const TRANSITION_CROSSFADE_SECONDS = 0.15;
/** How long each idle-arms variant (breathing / breathing-look) holds before cycling to the next — polish, not precision-critical. */
const IDLE_CYCLE_SECONDS = 4;

/**
 * Idle-variety one-shots (2026-09-09): "look around", "stretch", "jump", and
 * "clear ground with foot" (scuff), fired at random while the player stands
 * unarmed and idle — a flourish, not gameplay state, so plain `Math.random()`
 * is fine here (contrast the deterministic `phaseFromId` used for NPC idle
 * offsets, which DOES need to be reproducible across reloads).
 *
 * `lookAround` has no legs file — it's ARMS-only (the gaze/torso sweep), so
 * the legs slot stays on its normal `idle-1-breathing` constant pose and only
 * the arms slot needs a one-shot swap. The other three are synchronized
 * legs+arms pairs authored together in Blender (author_locomotion_idles.py) —
 * both slots must start and stop together or the hips/limbs desync.
 */
type IdleVarietyKind = 'lookAround' | 'stretch' | 'jump' | 'scuff';
const IDLE_VARIETY: Readonly<Record<IdleVarietyKind, { legs: LegsClipName | null; arms: ArmsClipName }>> = {
  lookAround: { legs: null, arms: 'idle-4-look-around-arms' },
  stretch: { legs: 'idle-3-stretch-legs', arms: 'idle-3-stretch-arms' },
  jump: { legs: 'idle-5-jump-legs', arms: 'idle-5-jump-arms' },
  scuff: { legs: 'idle-6-scuff-legs', arms: 'idle-6-scuff-arms' },
};
const IDLE_VARIETY_KINDS = Object.keys(IDLE_VARIETY) as readonly IdleVarietyKind[];
/** Random gap (seconds) of continuous idle before the next variety clip can fire. */
const IDLE_VARIETY_MIN_GAP = 8;
const IDLE_VARIETY_MAX_GAP = 20;

/**
 * Indirection for idle-variety's two random draws (gap + kind pick) —
 * deliberately NOT calling `Math.random()` directly at either call site.
 * `THREE.MathUtils.generateUUID()` (used by every `AnimationClip`/`Object3D`
 * constructor, including the clips this module loads) ALSO consumes
 * `Math.random()`. A test that mocks the global to a constant value, to get a
 * deterministic idle-variety pick, silently corrupts every UUID generated
 * while the mock is active — they collapse to the same string — which then
 * corrupts `AnimationMixer`'s clip-uuid action cache: a second, genuinely
 * different clip silently reuses the FIRST clip's cached action instead of
 * getting its own (confirmed by chasing an idle-variety test failure to
 * `mixer.clipAction()` returning `idle-1-breathing`'s action for a request
 * keyed on `idle-5-jump-legs`'s clip). Routing through this swappable seam
 * lets a test control ONLY this module's randomness and leaves THREE's own
 * `Math.random()` usage — and therefore every clip's UUID — alone.
 */
let idleVarietyRandom: () => number = () => Math.random();
/** Test-only override — see `idleVarietyRandom`'s doc comment. Pass `null` to restore real `Math.random()`. */
export function __setIdleVarietyRandomForTests(fn: (() => number) | null): void {
  idleVarietyRandom = fn ?? ((): number => Math.random());
}

function randomIdleVarietyGap(): number {
  return IDLE_VARIETY_MIN_GAP + idleVarietyRandom() * (IDLE_VARIETY_MAX_GAP - IDLE_VARIETY_MIN_GAP);
}

/** Per-frame state this module needs — a subset of what index.ts's frame loop already tracks as local closure variables (character-animation-runtime.md's brief: read them from there, don't re-plumb a second channel). */
export interface PlayerAnimInput {
  moving: boolean;
  gait: 'walk' | 'run';
  crouched: boolean;
  aiming: boolean;
  dialogOpen: boolean;
}

export interface PlayerAnimator {
  /** Advances the mixer and re-derives/crossfades pose state. Call once per frame with `dt` in seconds. */
  update(dt: number, input: PlayerAnimInput): void;
  /**
   * Damage-taken reaction: chest/shoulders (and whatever's parented to
   * hand.R) snap backward briefly, then settle back into whatever pose was
   * already playing. See `HIT_REACT_ARMS_CLIP`'s doc comment. No trigger
   * calls this yet — no damage/combat event exists in the client runtime —
   * this is the API a future one calls.
   */
  playHitReact(): void;
  /**
   * Attack one-shot — firearm recoil or melee swing for the held family.
   * No-ops when unarmed (no such asset). See `ATTACK_ARMS_CLIP`.
   */
  playAttack(): void;
  /**
   * Terminal death: the body falls and HOLDS the last frame, forever.
   *
   * Unlike `playHitReact`/`playAttack` this is not a one-shot the pose machine
   * recovers from — once it runs, normal legs/arms derivation stops for the
   * life of the animator. A downed fighter who stands back up into an idle
   * breathing loop is worse than no death animation at all.
   *
   * Idempotent: calling it again does nothing, which matters because the
   * caller drives it from synced HP that stays at 0 for every subsequent
   * frame.
   */
  playDeath(): void;
  dispose(): void;
}

const NOOP_ANIMATOR: PlayerAnimator = {
  update: () => {}, playHitReact: () => {}, playAttack: () => {}, playDeath: () => {}, dispose: () => {},
};

/**
 * Builds a per-instance animator bound to `root` (the object passed as
 * `sceneHandles.player` — see `render/scene.ts`). Returns a no-op animator
 * if `root` contains no `SkinnedMesh` (the `buildHumanoid()` fallback
 * primitive has no skeleton to bind a mixer to), so callers can call
 * `update()`/`dispose()` unconditionally without branching on model type.
 */
/**
 * Where a player animator reads the equipment it should put on the body.
 *
 * ─── Why this is injectable rather than just `gameStateStore` ───────────────
 * `PlayerAnimatorImpl` read the store directly, in five places, and that store
 * holds exactly one subject: the LOCAL player. It was the right shape while
 * the only animated player on screen was yours.
 *
 * The arena then built a second one for the OPPONENT — `arena-view.ts` uses
 * `createPlayerAnimator` for them deliberately ("the opponent IS a player, and
 * only the player animator has a locomotion state machine") — and inherited
 * the store with it. So the opponent wore YOUR hat, YOUR jacket and held YOUR
 * rifle, updating live as you switched weapons. It is invisible in a duel
 * against a bot with no gear, which is every duel there has been; it becomes
 * glaring the moment bots are dressed, which is what this pass does.
 *
 * So the source is a parameter, defaulting to the store. Nothing about the
 * local player changes.
 */
export interface AnimatorEquipmentSource {
  /** The equipped slots to render, in the same shape `EquipmentSheet.slots` uses. */
  slots(): Partial<Record<string, EquippedItemSummary>> | undefined;
  /** Notify on change. Return an unsubscribe. */
  subscribe(onChange: () => void): () => void;
}

/** The default source: the local player's own sheet. */
export const LOCAL_EQUIPMENT_SOURCE: AnimatorEquipmentSource = {
  slots: () => gameStateStore.get().equipment?.slots as Partial<Record<string, EquippedItemSummary>> | undefined,
  subscribe: (onChange) => gameStateStore.subscribe(onChange),
};

export function createPlayerAnimator(
  root: THREE.Object3D,
  equipment: AnimatorEquipmentSource = LOCAL_EQUIPMENT_SOURCE,
): PlayerAnimator {
  let skinned: THREE.SkinnedMesh | null = null;
  root.traverse((o) => {
    if (!skinned && (o as THREE.SkinnedMesh).isSkinnedMesh) skinned = o as THREE.SkinnedMesh;
  });
  if (!skinned) return NOOP_ANIMATOR;
  return new PlayerAnimatorImpl(root, skinned, equipment);
}

class PlayerAnimatorImpl implements PlayerAnimator {
  private readonly mixer: THREE.AnimationMixer;
  /** Where this body's clothes and weapon come from — see `AnimatorEquipmentSource`. */
  private readonly equipment: AnimatorEquipmentSource;
  private readonly handBone: THREE.Bone | null;
  /** This body's own palm, measured once at construction — see `anchoredGripPosition`. */
  private readonly palmLocal: THREE.Vector3 | null;
  private readonly headBone: THREE.Bone | null;
  /** Head-weighted vertices, head-local, for the headgear fit (measured once). */
  private readonly headPoints: THREE.Vector3[];
  /** Whole body in head bind space, for face-worn props; measured on first use. */
  private headMesh: BoneLocalMesh | null | undefined;
  private readonly skinned: THREE.SkinnedMesh;
  private readonly chestBone: THREE.Bone | null;
  private readonly legsActions = new Map<LegsClipName, THREE.AnimationAction>();
  private readonly armsActions = new Map<ArmsClipName, THREE.AnimationAction>();
  private readonly unsubEquipment: () => void;

  private disposed = false;
  private idleCycleT = 0;
  /** Terminal — see `playDeath`. Once true, `update` never derives a pose again. */
  private dead = false;

  private currentLegsAction: THREE.AnimationAction | null = null;
  private currentLegsName: LegsClipName | null = null;
  private legsTransitioning = false;
  private prevCrouched = false;

  private currentArmsAction: THREE.AnimationAction | null = null;
  private currentArmsName: ArmsClipName | null = null;
  /** Arms clips whose load has been kicked off — prevents re-requesting one every frame while it's in flight. */
  private readonly armsRequested = new Set<ArmsClipName>();

  private lastWeaponKey: string | null = null;
  private weaponRequestId = 0;
  private attachedWeapon: THREE.Object3D | null = null;
  /**
   * Whether the AIM grip (rather than the idle-armed one) is currently
   * seated on `attachedWeapon`. Tracked so the re-seat happens on the
   * aim EDGE only — `applyWeaponGrip` is idempotent, but running it every
   * frame would overwrite the weapon's transform after the mixer had
   * written it, which is a different bug wearing the same clothes.
   */
  private weaponGripAiming = false;
  /**
   * The pose family the ARMS are using, kept so `applyWeaponGrip` resolves
   * the grip against the same family the clip was chosen with.
   *
   * Deliberately not re-derived from `WEAPON_POSE_FAMILY_BY_KEY` inside
   * `applyWeaponGrip`: that is a hand-written key->family table, while the
   * player's clip comes from `armsPoseFamilyFor`, which reads `kind`/
   * `ammoType` off the ItemDef. They agree today, and a table that has to
   * keep agreeing with a function is exactly how a clip and its grip drift
   * onto different families — the one failure this pairing exists to stop.
   */
  private poseFamily: ArmsPoseFamily = 'unarmed';

  private lastHeadgearKey: string | null = null;
  private headgearRequestId = 0;
  private attachedHeadgear: THREE.Object3D | null = null;

  private lastTorsoKey: string | null = null;
  private torsoRequestId = 0;
  private attachedTorso: THREE.Object3D | null = null;

  private lastBackpackKey: string | null = null;
  private backpackRequestId = 0;
  private attachedBackpack: THREE.Object3D | null = null;

  // ---- skinned garments (see GARMENT_KEYS) ----
  // Keyed by slot rather than one field per slot, because unlike the weapon/
  // headgear/torso trio above these all run the SAME code path — a fourth
  // copy-pasted `lastXKey`/`xRequestId`/`attachedX` triple is what this
  // replaces, and what the next slot would otherwise add a fifth of.
  private readonly lastGarmentKey = new Map<EquipmentSlot, string | null>();
  private readonly garmentRequestId = new Map<EquipmentSlot, number>();
  private readonly attachedGarment = new Map<EquipmentSlot, THREE.Object3D>();
  private readonly skeleton: THREE.Skeleton;
  /** Hides the skin under worn garments — see body-regions.ts. */
  private readonly bodyMask: BodyRegionMask | null;

  // ---- idle-variety one-shots (see IDLE_VARIETY) ----
  private idleIdleSeconds = 0;
  private nextIdleVarietyGap = randomIdleVarietyGap();
  private playingVariety: IdleVarietyKind | null = null;
  private varietyArmsAction: THREE.AnimationAction | null = null;

  // ---- hit-react one-shot (see HIT_REACT_ARMS_CLIP) ----
  private playingHitReact: THREE.AnimationAction | null = null;
  private playingAttack: THREE.AnimationAction | null = null;

  constructor(
    private readonly root: THREE.Object3D,
    skinned: THREE.SkinnedMesh,
    equipment: AnimatorEquipmentSource = LOCAL_EQUIPMENT_SOURCE,
  ) {
    this.equipment = equipment;
    this.skinned = skinned;
    this.mixer = new THREE.AnimationMixer(root);
    this.skeleton = skinned.skeleton;
    this.bodyMask = BodyRegionMask.create(skinned);
    this.handBone = skinned.skeleton.getBoneByName(HAND_R_BONE) ?? null;
    if (!this.handBone) {
      console.warn(`[character-animations] no bone named "${HAND_R_BONE}" (sanitized from "hand.R") on this skeleton — weapon attach disabled`);
    }
    this.palmLocal = this.handBone ? measureBoneLocalCentroid(skinned, this.handBone) : null;
    this.headBone = skinned.skeleton.getBoneByName(HEAD_BONE) ?? null;
    this.headPoints = this.headBone ? measureBoneLocalPoints(skinned, this.headBone) : [];
    if (!this.headBone) {
      console.warn(`[character-animations] no bone named "${HEAD_BONE}" (sanitized from "head") on this skeleton — headgear attach disabled`);
    }
    this.chestBone = skinned.skeleton.getBoneByName(CHEST_BONE) ?? null;
    if (!this.chestBone) {
      console.warn(`[character-animations] no bone named "${CHEST_BONE}" (sanitized from "chest") on this skeleton — torso attach disabled`);
    }

    for (const name of LEGS_CLIP_NAMES) {
      loadLegsClip(name)
        .then((clip) => { if (!this.disposed) this.legsActions.set(name, this.mixer.clipAction(clip)); })
        .catch((err) => console.error(`[character-animations] failed to load legs clip "${name}"`, err));
    }
    for (const name of EAGER_ARMS_CLIPS) this.requestArmsClip(name);

    this.mixer.addEventListener('finished', this.onActionFinished);
    // Fires immediately with current state (core/state/store.ts's `subscribe`
    // contract — same pattern hud.ts/paperdoll.ts use), so this both
    // initializes the weapon visual and reacts to every later equip/unequip.
    this.unsubEquipment = this.equipment.subscribe(this.onEquipmentChange);
  }

  update(dt: number, input: PlayerAnimInput): void {
    if (this.disposed) return;
    this.mixer.update(dt);
    // Death owns both slots permanently. The mixer still advances (the fall
    // has to play out, and `clampWhenFinished` holds the last frame after
    // that), but nothing below may crossfade away from it.
    if (this.dead) return;
    this.idleCycleT += dt;

    const slots = this.equipment.slots();
    // PRIMARY, else SECONDARY, else unarmed — same resolution order as
    // `battleground.service.ts`'s combat-side weapon pick. Without the
    // SECONDARY fallback, every item whose ItemDef locks it to that slot
    // (pistol, snub_revolver, hunting_knife — see prisma/seed.ts) could be
    // equipped but could never render or pose: nothing else can occupy
    // PRIMARY for them, and this only ever read PRIMARY.
    const held = slots?.PRIMARY ?? slots?.SECONDARY;
    const poseFamily = armsPoseFamilyFor(held);
    this.poseFamily = poseFamily;

    // A one-shot variety clip owns BOTH slots until it (or an interruption)
    // ends it — normal legs/arms derivation would otherwise immediately
    // crossfade away from it every frame (its target clip differs from
    // whatever `desiredLegsClip`/`desiredArmsClip` compute while standing
    // idle). See updateIdleVariety's doc comment.
    if (this.updateIdleVariety(dt, input, poseFamily)) return;

    // Keep the weapon's grip in step with the pose the arms are about to
    // take. Edge-triggered, and BEFORE `updateArms` so the clip and the grip
    // land on the same frame rather than the weapon lagging the hands by one.
    const wantAimGrip = input.aiming && poseFamily !== 'unarmed';
    if (wantAimGrip !== this.weaponGripAiming) {
      this.weaponGripAiming = wantAimGrip;
      this.applyWeaponGrip();
    }

    this.updateLegs(input);
    // A hit-react or attack one-shot owns the arms slot until onActionFinished
    // ends it (legs keep moving normally — only the arms play the one-shot) —
    // see playHitReact / playAttack.
    if (this.playingHitReact || this.playingAttack) return;
    this.updateArms(input, poseFamily);
  }

  dispose(): void {
    this.disposed = true;
    this.unsubEquipment();
    this.mixer.removeEventListener('finished', this.onActionFinished);
    if (this.attachedWeapon) {
      this.attachedWeapon.removeFromParent();
      this.attachedWeapon = null;
    }
    if (this.attachedHeadgear) {
      this.attachedHeadgear.removeFromParent();
      this.attachedHeadgear = null;
    }
    if (this.attachedTorso) {
      this.attachedTorso.removeFromParent();
      this.attachedTorso = null;
    }
    this.removeBackpack();
    for (const garment of this.attachedGarment.values()) garment.removeFromParent();
    this.attachedGarment.clear();
    this.bodyMask?.restore();
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.root);
  }

  // ---- legs slot ----

  private updateLegs(input: PlayerAnimInput): void {
    const crouchedChanged = input.crouched !== this.prevCrouched;
    if (crouchedChanged) {
      const played = this.tryPlayLegsTransition(input.crouched);
      // Only commit the new prevCrouched once the transition actually starts
      // (i.e. `stand-to-crouch-legs` is loaded) — if it's not loaded yet,
      // retry the same edge next frame instead of silently dropping it.
      if (played) this.prevCrouched = input.crouched;
      return;
    }
    if (this.legsTransitioning) return; // mid one-shot — don't fight it with a steady-state crossfade
    this.setLegsClip(this.desiredLegsClip(input));
  }

  private desiredLegsClip(input: PlayerAnimInput): LegsClipName {
    if (input.moving && input.crouched) return 'crouch-move-legs';
    if (input.moving) return input.gait === 'walk' ? 'walk-legs' : 'run-legs';
    if (input.crouched) return 'crouch-idle-legs';
    return 'idle-1-breathing'; // stripped to LEGS_BONES — a constant standing pose; idle 1/2/3 variety lives on the arms slot, see updateArms()
  }

  private tryPlayLegsTransition(enteringCrouch: boolean): boolean {
    const action = this.legsActions.get('stand-to-crouch-legs');
    if (!action) return false;
    const clip = action.getClip();

    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    // "Playable in reverse for crouch->stand" (asset_todo.md) — negative
    // timeScale from the clip's own end time; three.js still dispatches
    // `finished` at time 0 in this direction.
    action.timeScale = enteringCrouch ? 1 : -1;
    action.time = enteringCrouch ? 0 : clip.duration;
    action.play();

    if (this.currentLegsAction) this.currentLegsAction.crossFadeTo(action, TRANSITION_CROSSFADE_SECONDS, false);
    else action.fadeIn(TRANSITION_CROSSFADE_SECONDS);

    this.currentLegsAction = action;
    this.currentLegsName = null; // sentinel: mid-transition, not a steady-state clip
    this.legsTransitioning = true;
    return true;
  }

  private setLegsClip(name: LegsClipName): void {
    if (name === this.currentLegsName) return;
    const action = this.legsActions.get(name);
    if (!action) return; // not loaded yet — keep whatever's currently playing, retried again next frame
    this.crossfadeSlot(this.currentLegsAction, action, CROSSFADE_SECONDS, true);
    this.currentLegsAction = action;
    this.currentLegsName = name;
  }

  // ---- arms slot ----

  private updateArms(input: PlayerAnimInput, poseFamily: ArmsPoseFamily): void {
    this.setArmsClip(this.desiredArmsClip(input, poseFamily));
  }

  private desiredArmsClip(input: PlayerAnimInput, poseFamily: ArmsPoseFamily): ArmsClipName {
    // Highest priority: dialog freezes the sim's velocity (base-sim.ts) but
    // not `crouched` — talking pose always wins regardless of locomotion state.
    if (input.dialogOpen) return 'idle-talking-arms';

    // Aiming (§3.7): whole-rig facing already locks to the cursor via
    // `rotation.y`; the arms slot raises the weapon into the AIM hold.
    //
    // Families with no authored aim clip fall back to their idle-armed hold
    // inside `resolveArmedPose` — that is every family but `rifle` today, so
    // a pistol still shows the low carry. The fallback is deliberate rather
    // than a placeholder pose: an aim clip is an IK-solved asset per family
    // (`author_aim_rifle.py`), and the one time a family got an eyeballed
    // one it shipped with both arms straight out in front.
    //
    // The GRIP that pairs with this clip is applied by `applyWeaponGrip`,
    // driven from the same `resolveArmedPose` call — see its doc comment for
    // why the two must never be swapped independently.
    if (input.aiming) {
      if (poseFamily !== 'unarmed') return resolveArmedPose(this.lastWeaponKey ?? '', poseFamily, true).arms;
      return input.crouched ? 'crouch-idle-arms' : this.idleArmsCycleClip();
    }

    // Locomotion, for EVERY family. Armed families used to fall through to a
    // static idle-armed-* hold here because no armed locomotion clips existed
    // (asset_todo.md §7C); they were authored later, so a moving armed
    // character now swings its arms in the matching family instead of gliding
    // along in a frozen ready pose.
    if (input.moving) {
      const set = LOCOMOTION_ARMS_CLIP[poseFamily];
      if (input.crouched) return set.crouchMove;
      return input.gait === 'walk' ? set.walk : set.run;
    }

    // Stationary: the armed hold, or the unarmed idle cycle. Crouched unarmed
    // gets the hunch clip (crouch rework 2026-09-10) — the "back bended, head
    // lower" half of the pose lives on the ARMS layer, since spine/chest/head
    // are arms-slot bones.
    if (poseFamily !== 'unarmed') return IDLE_ARMED_CLIP[poseFamily];
    return input.crouched ? 'crouch-idle-arms' : this.idleArmsCycleClip();
  }

  /** Cycles idle-1 (breathing) / idle-2 (breathing + look-around) for stationary variety — used for BOTH standing and crouched idle, since an arms-only clip is agnostic to what the legs are doing (the whole point of the split). `idle-3-stretch` is deferred: it has no `-arms` derivative because its legs genuinely animate (tip-toe lift), so it can't split — see character-animation-runtime.md's Phase 1 report. */
  private idleArmsCycleClip(): ArmsClipName {
    const cycle = Math.floor(this.idleCycleT / IDLE_CYCLE_SECONDS) % 2;
    return cycle === 0 ? 'idle-1-breathing-arms' : 'idle-2-breathing-look-arms';
  }

  /**
   * Starts loading one arms clip, at most once per animator. `loadRawClip`'s
   * cache is module-level, so a second animator (or a re-request after a
   * dispose) reuses the same in-flight promise rather than refetching.
   */
  private requestArmsClip(name: ArmsClipName): void {
    if (this.armsRequested.has(name)) return;
    this.armsRequested.add(name);
    loadArmsClip(name)
      .then((clip) => { if (!this.disposed) this.armsActions.set(name, this.mixer.clipAction(clip)); })
      .catch((err) => console.error(`[character-animations] failed to load arms clip "${name}"`, err));
  }

  private setArmsClip(name: ArmsClipName): void {
    if (name === this.currentArmsName) return;
    const action = this.armsActions.get(name);
    if (!action) {
      // Not loaded. For the armed families that's the NORMAL first-use path
      // (they're lazy — see EAGER_ARMS_CLIPS), so kick the load off and keep
      // whatever is currently playing; the next frame retries and crossfades
      // in as soon as it lands. Roughly one held frame's worth of the previous
      // pose, versus ~1 MB of clips fetched up front for weapons the player
      // may never equip.
      this.requestArmsClip(name);
      return;
    }
    this.crossfadeSlot(this.currentArmsAction, action, CROSSFADE_SECONDS, true);
    this.currentArmsAction = action;
    this.currentArmsName = name;
  }

  // ---- shared crossfade helper ----

  /**
   * `asOneShot`: skip the loop-repeat/clamp-false setup and `to.reset()`+
   * `to.play()` — the caller (idle-variety) already configured those for a
   * `LoopOnce` one-shot before calling this, and `to.reset()` here would
   * re-zero its start time right after; `to.setLoop(LoopRepeat, ...)` would
   * silently turn the one-shot into an infinite loop that never dispatches
   * `finished` (found this exact way: an idle-variety clip never ended).
   */
  private crossfadeSlot(from: THREE.AnimationAction | null, to: THREE.AnimationAction, seconds: number, warp: boolean, asOneShot = false): void {
    if (!asOneShot) {
      to.reset();
      to.setLoop(THREE.LoopRepeat, Infinity);
      to.clampWhenFinished = false;
      to.play();
    }
    if (from) from.crossFadeTo(to, seconds, warp);
    else to.fadeIn(seconds);
  }

  // ---- idle-variety one-shots ----

  /**
   * Fires while standing unarmed and idle with nothing else going on; after a
   * random gap, plays one of IDLE_VARIETY's clips once on both slots. Returns
   * true while a variety clip owns the slots (including the frame it just
   * started) — the caller must skip normal legs/arms derivation in that case.
   *
   * `Math.random()` is fine here — this is a cosmetic flourish, not sim state
   * (contrast `phaseFromId`'s deterministic NPC offsets below, which matter
   * for reproducibility across reloads).
   */
  private updateIdleVariety(dt: number, input: PlayerAnimInput, poseFamily: ArmsPoseFamily): boolean {
    const canIdle = !input.moving && !input.crouched && !input.aiming && !input.dialogOpen && poseFamily === 'unarmed';

    if (this.playingVariety) {
      if (canIdle) return true; // let it run to completion — onActionFinished ends it
      // Interrupted (player moved/crouched/aimed/opened dialog/equipped a
      // weapon mid-clip): abandon it. Leave currentLegsAction/currentArmsAction
      // pointing at wherever the variety clip was — clearing only the *Name
      // sentinels forces updateLegs/updateArms to recompute and crossFadeTo
      // FROM that in-progress pose, which blends smoothly rather than popping.
      this.playingVariety = null;
      this.varietyArmsAction = null;
      this.currentArmsName = null;
      this.currentLegsName = null;
      return false;
    }

    if (!canIdle || this.legsTransitioning) {
      this.idleIdleSeconds = 0;
      return false;
    }
    this.idleIdleSeconds += dt;
    if (this.idleIdleSeconds < this.nextIdleVarietyGap) return false;

    this.idleIdleSeconds = 0;
    this.nextIdleVarietyGap = randomIdleVarietyGap();
    const kind = IDLE_VARIETY_KINDS[Math.floor(idleVarietyRandom() * IDLE_VARIETY_KINDS.length)]!;
    this.tryPlayIdleVariety(kind);
    return this.playingVariety !== null;
  }

  private tryPlayIdleVariety(kind: IdleVarietyKind): void {
    const spec = IDLE_VARIETY[kind];
    const armsAction = this.armsActions.get(spec.arms);
    const legsAction = spec.legs ? this.legsActions.get(spec.legs) : undefined;
    if (!armsAction || (spec.legs && !legsAction)) return; // not loaded yet (both are eager-loaded — should be rare); try again next gap

    this.playingVariety = kind;
    this.varietyArmsAction = armsAction;

    armsAction.reset();
    armsAction.setLoop(THREE.LoopOnce, 1);
    armsAction.clampWhenFinished = true; // hold the last frame — onActionFinished re-derives the very same tick
    armsAction.play();
    this.crossfadeSlot(this.currentArmsAction, armsAction, CROSSFADE_SECONDS, false, true);
    this.currentArmsAction = armsAction;
    this.currentArmsName = spec.arms;

    if (legsAction) {
      legsAction.reset();
      legsAction.setLoop(THREE.LoopOnce, 1);
      legsAction.clampWhenFinished = true;
      legsAction.play();
      this.crossfadeSlot(this.currentLegsAction, legsAction, CROSSFADE_SECONDS, false, true);
      this.currentLegsAction = legsAction;
      this.currentLegsName = null; // sentinel: mid one-shot, like the crouch transition
    }
  }

  // ---- hit-react one-shot (see HIT_REACT_ARMS_CLIP) ----

  /**
   * Plays the backward-flinch reaction on the arms slot, in whatever family
   * is currently held (legs are untouched — see `update()`'s guard). Reuses
   * exactly the one-shot machinery `tryPlayIdleVariety` established:
   * `LoopOnce` + `clampWhenFinished` + `crossfadeSlot(..., asOneShot=true)`,
   * ended by `onActionFinished`.
   *
   * No-ops (silently skips this hit) if a crouch transition or idle-variety
   * clip already owns the arms slot — a flinch mid-transition would fight
   * it — or if the current family's clip isn't loaded yet, in which case it
   * kicks off the load (same lazy-load path every armed clip uses) so the
   * NEXT hit lands.
   */
  playHitReact(): void {
    if (this.disposed || this.legsTransitioning || this.playingVariety || this.playingHitReact) return;
    // Being shot mid-swing interrupts the swing. Dropping the reference (not
    // just letting it run) matters: the attack's own 'finished' event would
    // otherwise fire partway through this flinch and clear `currentArmsName`,
    // re-deriving the arms slot while the flinch is still playing.
    this.playingAttack = null;
    const slots = this.equipment.slots();
    const held = slots?.PRIMARY ?? slots?.SECONDARY;
    const name = HIT_REACT_ARMS_CLIP[armsPoseFamilyFor(held)];
    const action = this.armsActions.get(name);
    if (!action) {
      this.requestArmsClip(name);
      return;
    }

    this.playingHitReact = action;
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.play();
    this.crossfadeSlot(this.currentArmsAction, action, CROSSFADE_SECONDS, false, true);
    this.currentArmsAction = action;
    this.currentArmsName = null; // sentinel: mid one-shot, like idle-variety and the crouch transition
  }

  /**
   * Plays the attack one-shot — firearm recoil or a melee swing, depending on
   * the held family — on the arms slot. Same one-shot machinery as
   * `playHitReact`, and the same silent-skip conditions.
   *
   * Two behaviours specific to attacks:
   *  - No-ops for an UNARMED character. `ATTACK_ARMS_CLIP` has no `unarmed`
   *    entry on purpose (no such asset exists); falling back to a melee swing
   *    would animate the character swinging a weapon they are not holding.
   *  - A hit-react in flight WINS. Being shot mid-swing should interrupt the
   *    swing, not be swallowed by it — and letting both own the arms slot is
   *    the failure mode, since whichever finishes second clears
   *    `currentArmsName` and re-derives while the other is still running.
   */
  playAttack(): void {
    if (this.disposed || this.legsTransitioning || this.playingVariety
      || this.playingHitReact || this.playingAttack) return;
    const slots = this.equipment.slots();
    const held = slots?.PRIMARY ?? slots?.SECONDARY;
    const name = ATTACK_ARMS_CLIP[armsPoseFamilyFor(held)];
    if (!name) return; // no clip for this family — see ATTACK_ARMS_CLIP
    const action = this.armsActions.get(name);
    if (!action) {
      this.requestArmsClip(name);
      return;
    }

    this.playingAttack = action;
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.play();
    this.crossfadeSlot(this.currentArmsAction, action, CROSSFADE_SECONDS, false, true);
    this.currentArmsAction = action;
    this.currentArmsName = null; // sentinel: mid one-shot, like idle-variety and the crouch transition
  }

  // ---- one-shot completion (stand-to-crouch-legs, idle-variety, hit-react, attack) ----

  private onActionFinished = (e: { action: THREE.AnimationAction }): void => {
    if (this.legsTransitioning && e.action === this.legsActions.get('stand-to-crouch-legs')) {
      this.legsTransitioning = false;
      this.currentLegsName = null; // force the next update() to re-derive and crossfade into the correct steady-state
      return;
    }
    if (this.playingHitReact && e.action === this.playingHitReact) {
      this.playingHitReact = null;
      this.currentArmsName = null; // force the next update() to re-derive and crossfade back to steady state
      return;
    }
    if (this.playingAttack && e.action === this.playingAttack) {
      this.playingAttack = null;
      this.currentArmsName = null; // force the next update() to re-derive and crossfade back to steady state
      return;
    }
    if (this.playingVariety && e.action === this.varietyArmsAction) {
      // The arms half always exists (every IDLE_VARIETY entry has one); the
      // legs half, when present, is the same duration by construction (both
      // exported from one synchronized Blender action), so ending on the
      // arms event alone is sufficient — no separate legs 'finished' wait.
      this.playingVariety = null;
      this.varietyArmsAction = null;
      this.currentArmsName = null; // force the next update() to re-derive and crossfade back to steady idle
      // currentLegsName is already null if this kind had a legs half (set in
      // tryPlayIdleVariety); if not (lookAround), it was never touched.
    }
  };

  /**
   * Fall over and stay down. See the interface doc for why this is terminal.
   *
   * ─── Why the clips are loaded here rather than up front ──────────────────
   * `death-legs` is deliberately absent from `LEGS_CLIP_NAMES`, which is the
   * eager set: it plays at most once per session, at the very end, so paying
   * for it on every character spawn — including the settlement, where nobody
   * can die — is the wrong trade. It is fetched at the moment of death and
   * crossfaded in when it lands, which is a frame or two later and reads as
   * the body taking a moment to buckle.
   *
   * `this.dead` is set BEFORE the loads resolve, so locomotion stops
   * immediately even if the clip never arrives. A downed fighter frozen
   * mid-stride is wrong; a downed fighter still jogging is much worse.
   */
  playDeath(): void {
    if (this.disposed || this.dead) return;
    this.dead = true;
    // Clear the one-shot bookkeeping — a swing or a flinch in flight must not
    // fire its `finished` handler later and re-derive a pose over the corpse.
    this.playingAttack = null;
    this.playingHitReact = null;
    this.playingVariety = null;

    const startTerminal = (
      action: THREE.AnimationAction | null | undefined,
      from: THREE.AnimationAction | null,
    ): THREE.AnimationAction | null => {
      if (!action) return null;
      action.reset();
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;
      action.play();
      this.crossfadeSlot(from, action, CROSSFADE_SECONDS, false, true);
      return action;
    };

    const legs = this.legsActions.get('death-legs');
    if (legs) {
      this.currentLegsAction = startTerminal(legs, this.currentLegsAction);
    } else {
      loadLegsClip('death-legs')
        .then((clip) => {
          if (this.disposed) return;
          const action = this.mixer.clipAction(clip);
          this.legsActions.set('death-legs', action);
          this.currentLegsAction = startTerminal(action, this.currentLegsAction);
        })
        .catch((err) => console.error('[character-animations] failed to load death-legs', err));
    }
    this.currentLegsName = null;

    const arms = this.armsActions.get('death-arms');
    if (arms) {
      this.currentArmsAction = startTerminal(arms, this.currentArmsAction);
    } else {
      loadArmsClip('death-arms')
        .then((clip) => {
          if (this.disposed) return;
          const action = this.mixer.clipAction(clip);
          this.armsActions.set('death-arms', action);
          this.currentArmsAction = startTerminal(action, this.currentArmsAction);
        })
        .catch((err) => console.error('[character-animations] failed to load death-arms', err));
    }
    this.currentArmsName = null;
  }

  // ---- weapon attach (equipment-driven) ----

  private onEquipmentChange = (): void => {
    const slots = this.equipment.slots();
    const held = slots?.PRIMARY ?? slots?.SECONDARY;
    const key = held?.def.key ?? null;
    this.poseFamily = armsPoseFamilyFor(held);
    if (key !== this.lastWeaponKey) {
      this.lastWeaponKey = key;
      void this.updateWeaponVisual(key);
    }

    const headgearKey = slots?.HEADGEAR?.def.key ?? null;
    if (headgearKey !== this.lastHeadgearKey) {
      this.lastHeadgearKey = headgearKey;
      void this.updateHeadgearVisual(headgearKey);
    }

    const backpackKey = slots?.BACKPACK?.def.key ?? null;
    if (backpackKey !== this.lastBackpackKey) {
      this.lastBackpackKey = backpackKey;
      void this.updateBackpackVisual(backpackKey);
    }

    // A garment and a rigid torso prop are mutually exclusive for the same
    // key: `updateTorsoVisual` bails on anything in `GARMENT_KEYS`, so an item
    // migrated from the rigid table to a skinned GLB does not briefly render
    // as both.
    const torsoKey = slots?.TORSO?.def.key ?? null;
    if (torsoKey !== this.lastTorsoKey) {
      this.lastTorsoKey = torsoKey;
      void this.updateTorsoVisual(torsoKey);
    }

    // What is worn and what is DRAWN differ: a trench stops leg garments
    // rendering while they stay equipped. `lastGarmentKey` holds the drawn key.
    const worn = GARMENT_SLOTS.map((slot) => slots?.[slot]?.def.key ?? null);
    const drawn = new Set(drawnGarmentKeys(worn.filter((k): k is string => !!k)));
    for (const [i, slot] of GARMENT_SLOTS.entries()) {
      const wornKey = worn[i] ?? null;
      const key = wornKey && drawn.has(wornKey) ? wornKey : null;
      if (key !== (this.lastGarmentKey.get(slot) ?? null)) {
        this.lastGarmentKey.set(slot, key);
        void this.updateGarmentVisual(slot, key);
      }
    }
  };

  private async updateWeaponVisual(key: string | null): Promise<void> {
    const requestId = ++this.weaponRequestId;

    if (this.attachedWeapon) {
      this.attachedWeapon.removeFromParent();
      this.attachedWeapon = null;
    }
    if (!key || !this.handBone) return;

    const url = WEAPON_MODEL_BY_KEY[key];
    if (!url) return; // no visual mapped for this item yet — arms pose still swaps correctly, just nothing held

    try {
      const model = await loadPropModel(url);
      if (this.disposed || requestId !== this.weaponRequestId) return; // superseded by a later equip change, or torn down, while loading
      this.attachedWeapon = model;
      // Which catalog item this mesh IS, stamped on the object itself.
      // `loadPropModel` caches by URL and clones, so the scene graph otherwise
      // carries no way back to the key — and "is the body holding what the
      // room says it is holding" is a question worth being able to ask from
      // outside (`arena-view.ts`'s DEV `__duel.heldWeaponModel`, which is what
      // catches a hotbar swap that moves the server and not the model).
      model.userData.weaponKey = key;
      // Read the grip from the CURRENT aim state rather than assuming the
      // idle hold: the player can equip a rifle with RMB already down, and
      // the load is async, so by the time it resolves the aim pose may
      // already be playing. Same hazard the NPC constructor documents.
      this.applyWeaponGrip();
      attachToBone(this.handBone, model);
    } catch (err) {
      console.error(`[character-animations] failed to load weapon model "${url}"`, err);
    }
  }

  /**
   * Seat `attachedWeapon` with the grip for the pose the arms are in.
   *
   * The counterpart of `NpcAnimatorImpl.applyGrip`, and split out for the
   * same two callers: the async model load, and every change of aim state.
   * Offsets are SET, never accumulated, so repeated calls are idempotent.
   *
   * NOT divided by the bone's world scale — a grip position is already a
   * bone-local vector in rig units, so that scale multiplying it is the
   * point. See `attachToBone`'s doc comment on why only the SCALE is
   * cancelled, and `anchoredGripPosition` for the re-anchor onto this
   * body's own measured palm.
   */
  private applyWeaponGrip(): void {
    const model = this.attachedWeapon;
    const key = this.lastWeaponKey;
    if (!model || !key || !this.handBone) return;
    const grip = this.poseFamily === 'unarmed'
      ? WEAPON_GRIP_OFFSET[key] ?? {}
      : resolveArmedPose(key, this.poseFamily, this.weaponGripAiming).grip;
    const position = grip.position
      ? anchoredGripPosition(grip.position, this.palmLocal, this.handBone)
      : ([0, 0, 0] as const);
    model.position.set(...position);
    model.rotation.set(...(grip.rotation ?? [0, 0, 0]));
  }

  /** Same shape as {@link updateWeaponVisual}, HEADGEAR slot / head bone. */
  private async updateHeadgearVisual(key: string | null): Promise<void> {
    const requestId = ++this.headgearRequestId;

    if (this.attachedHeadgear) {
      this.attachedHeadgear.removeFromParent();
      this.attachedHeadgear = null;
    }
    if (!key || !this.headBone) return;

    const url = EQUIPMENT_MODEL_BY_KEY[key];
    if (!url) return; // no visual mapped for this item yet

    try {
      const model = await loadPropModel(url);
      if (this.disposed || requestId !== this.headgearRequestId) return;
      const offset = EQUIPMENT_GRIP_OFFSET[key];
      // Squat heads (Rook): fit the prop per axis about the head joint so it
      // seats on this body's crown and covers its fringe; face-worn props take
      // their depth from this body's nose — see `headgearFit` / `faceFront`.
      const worldScale = new THREE.Vector3();
      this.headBone.getWorldScale(worldScale);
      const boneScale = worldScale.x || REFERENCE_RIG_SCALE;
      const fit = headgearFit(this.headPoints, boneScale);
      if (offset?.position) {
        let face: Parameters<typeof headgearOffset>[2];
        if (offset.faceAnchor) {
          if (this.headMesh === undefined) this.headMesh = measureBoneLocalMesh(this.skinned, this.headBone);
          face = { anchor: offset.faceAnchor, mesh: this.headMesh, boneWorldScale: boneScale, height: FACE_HEIGHT_BY_BODY[this.skinned.name] };
        }
        const placed = headgearOffset(offset.position, fit, face);
        model.position.set(...fixedMetreOffset(placed, this.headBone));
      }
      if (offset?.rotation) model.rotation.set(...offset.rotation);
      attachToBone(this.headBone, model);
      model.scale.multiply(fit);
      this.attachedHeadgear = model;
    } catch (err) {
      console.error(`[character-animations] failed to load headgear model "${url}"`, err);
    }
  }

  private removeBackpack(): void {
    if (!this.attachedBackpack) return;
    this.attachedBackpack.removeFromParent();
    disposeBackpack(this.attachedBackpack);
    this.attachedBackpack = null;
  }

  /** Same shape as {@link updateTorsoVisual}, BACKPACK slot / chest bone. See `backpack-visuals.ts`. */
  private async updateBackpackVisual(key: string | null): Promise<void> {
    const requestId = ++this.backpackRequestId;
    this.removeBackpack();
    if (!key || !this.chestBone) return;
    try {
      const built = await buildBackpackModel(key, loadPropModel);
      if (!built) return; // the waist pouch, or a key with no visual
      if (this.disposed || requestId !== this.backpackRequestId) { disposeBackpack(built.model); return; }
      built.model.position.set(...fixedMetreOffset(built.offset, this.chestBone));
      attachToBone(this.chestBone, built.model);
      this.attachedBackpack = built.model;
    } catch (err) {
      console.error(`[character-animations] failed to load backpack "${key}"`, err);
    }
  }

  /** Same shape as {@link updateWeaponVisual}, TORSO slot / chest bone. */
  private async updateTorsoVisual(key: string | null): Promise<void> {
    const requestId = ++this.torsoRequestId;

    if (this.attachedTorso) {
      this.attachedTorso.removeFromParent();
      this.attachedTorso = null;
    }
    if (!key || !this.chestBone) return;
    if (GARMENT_KEYS.includes(key)) return; // skinned garment owns this key

    const url = TORSO_MODEL_BY_KEY[key];
    if (!url) return; // no visual mapped for this item yet

    try {
      const model = await loadPropModel(url);
      if (this.disposed || requestId !== this.torsoRequestId) return;
      const offset = TORSO_GRIP_OFFSET[key];
      if (offset?.position) model.position.set(...fixedMetreOffset(offset.position, this.chestBone));
      if (offset?.rotation) model.rotation.set(...offset.rotation);
      attachToBone(this.chestBone, model);
      this.attachedTorso = model;
    } catch (err) {
      console.error(`[character-animations] failed to load torso model "${url}"`, err);
    }
  }

  /**
   * The skinned counterpart to the three rigid `update*Visual` methods above.
   * One method for every clothing slot — see the field comment — and no bone
   * or offset anywhere, because the skeleton places the geometry.
   */
  private async updateGarmentVisual(slot: EquipmentSlot, key: string | null): Promise<void> {
    const requestId = (this.garmentRequestId.get(slot) ?? 0) + 1;
    this.garmentRequestId.set(slot, requestId);

    const previous = this.attachedGarment.get(slot);
    if (previous) {
      previous.removeFromParent();
      this.attachedGarment.delete(slot);
    }
    // Skin comes back the moment the garment goes, never a hole in the body.
    this.refreshBodyMask();
    if (!key || !GARMENT_KEYS.includes(key)) return;

    const url = garmentUrl(key);
    try {
      const loaded = await loadGarmentRoot(url);
      if (this.disposed || requestId !== this.garmentRequestId.get(slot)) return;
      // SkeletonUtils.clone, not Object3D.clone: a plain clone shares the
      // geometry (fine) but also the `skeleton` reference, so two characters
      // wearing the same garment would fight over one rebind — the second
      // equip would re-point the first character's mesh at the second
      // character's bones. The cache holds the pristine parse; every equip
      // gets its own copy to bind.
      const copy = SkeletonUtils.clone(loaded);
      const garment = bindGarmentToSkeleton(copy, this.skeleton);
      if (!garment) return;
      applyGarmentColors(garment, key);
      this.root.add(garment);
      this.attachedGarment.set(slot, garment);
      this.refreshBodyMask();
    } catch (err) {
      console.error(`[character-animations] failed to load garment "${url}"`, err);
      this.refreshBodyMask();
    }
  }

  /**
   * Skin is hidden only under garments that are actually attached, so a slot
   * still loading its garment shows skin, not a gap.
   */
  private refreshBodyMask(): void {
    if (!this.bodyMask) return;
    const worn: string[] = [];
    for (const slot of this.attachedGarment.keys()) {
      const key = this.lastGarmentKey.get(slot);
      if (key) worn.push(key);
    }
    this.bodyMask.setCovered(coveredRegions(worn));
  }
}

// ─── NPC held items (props, not player equipment) ───────────────────────────

/**
 * Static props an NPC can be shown holding — placed via `WorldObject.
 * heldItemKey` (`ultra_map_editor`'s NPC panel), independent of the player's
 * `gameStateStore`-driven equipment system above (an NPC has no inventory).
 * Model + grip are a fixed pair per key, unlike the player's
 * `WEAPON_MODEL_BY_KEY`/`WEAPON_GRIP_OFFSET` split, because nothing here is
 * ever re-skinned onto a different visual at runtime.
 *
 * `rake`'s grip was derived against `work-raking-arms.glb`'s first keyframe
 * with `derive_grips_gltf.py`'s glTF-space method (composing `hand.R`'s posed
 * world matrix from the shipped nodes, same as the player table above) rather
 * than guessed — but it's a first pass calibrated at one frame of a clip that
 * sweeps the arm through a full stroke, so the fit will drift some at the
 * other end of the swing. Flag for a playtest visual pass, same as every
 * other first-authored pose in this file.
 */
const NPC_HELD_ITEM: Readonly<Record<string, {
  url: string;
  position?: readonly [number, number, number];
  rotation?: readonly [number, number, number];
}>> = {
  rake: {
    url: '/models/npc-props/prop_rake.glb',
    // Derived by author_work_raking.py from the shipped work-raking clip pair
    // at t=0, in pure glTF space (hand.R composed from char-4-rigged.glb nodes
    // + BOTH clips' keyframes — the derive_grips_gltf.py method). The v1
    // numbers were off by the metres->rig-units factor (position skipped the
    // /1.8035 the weapon table applies via SIZES), which is why the rake read
    // as disconnected from the hand. Verified numerically from these exact
    // numbers against the shipped bytes: worst tine-height drift over the
    // loop 0.3 cm world, worst left-palm-to-shaft gap 2.4 cm, max per-frame
    // rotation step of any arm bone 0.12 rad (the v1 left arm shipped a
    // 1.27 rad/frame euler-branch sweep — see the authoring script docstring).
    position: [-0.24620, -0.05341, -0.04985],
    rotation: [-0.52089, -0.40743, -1.36145],
  },
};

/**
 * Read by the PLAYER too since 2026-09-19 (`armsPoseFamilyFor`), hence no
 * `NPC_` prefix any more — see that function for the `ammoType` bug this
 * being the single source of truth fixes.
 *
 * A `heldItemKey` that's actually a player weapon key (`WEAPON_MODEL_BY_KEY`)
 * -> its `ArmsPoseFamily`. Lets an NPC hold the exact same weapon models the
 * player can equip, using the exact same `WEAPON_GRIP_OFFSET` numbers (§2 of
 * the doc comment above: the offsets are pure rig-local `hand.R` geometry,
 * with no player-specific dependency — only the ARM POSE the grip was solved
 * against matters, which is why this table exists at all: it picks the
 * matching `idle-armed-<family>-arms`/`aim-<family>-arms` clip so the grip
 * numbers land on the pose they were actually calibrated for).
 */
export const WEAPON_POSE_FAMILY_BY_KEY: Readonly<Record<string, ArmedPoseFamily>> = {
  rifle_bolt: 'rifle',
  carbine: 'rifle',
  shotgun: 'rifle',
  pistol: 'pistol',
  snub_revolver: 'pistol',
  hunting_knife: 'knife',
  axe: 'axe',
  hockey_stick: 'blunt',
  // full-catalog batch (2026-09-10) — every remaining /models/weapons GLB.
  ak_bullpup: 'rifle',
  akm: 'rifle',
  auto_shotgun: 'rifle',
  fn2000: 'rifle',
  frf2: 'rifle',
  garand: 'rifle',
  m16: 'rifle',
  m249: 'rifle',
  m82: 'rifle',
  mp4: 'rifle',
  mp5: 'rifle',
  mp7: 'rifle',
  p90: 'rifle',
  pkm: 'rifle',
  ppsh: 'rifle',
  sawed_off: 'rifle',
  scar: 'rifle',
  scar_alt: 'rifle',
  scar_scope: 'rifle',
  svd: 'rifle',
  ump45: 'rifle',
  vss: 'rifle',
  g36: 'rifle',
  mac10: 'pistol',
  magnum: 'pistol',
  bat: 'blunt',
  crowbar: 'axe',
  dynamite: 'knife',
  flail: 'blunt',
  machete: 'knife',
  maul: 'blunt',
  plank: 'blunt',
  sledgehammer: 'blunt',
  // Premium weapons (2026-10-19). The Thompson fires .45 (pistol ammo) and is
  // still held two-handed: the rifle hold, declared here so ammoType never decides.
  fire_axe_red: 'axe',
  fire_axe_hazard: 'axe',
  fire_axe_forged: 'axe',
  sword_spring: 'knife',
  sword_blued: 'knife',
  pistol_nickel: 'pistol',
  deagle: 'pistol',
  mp5_urban: 'rifle',
  tommygun: 'rifle',
  akm_raider: 'rifle',
  scar_woodland: 'rifle',
  m16_rattlecan: 'rifle',
  pkm_arctic: 'rifle',
};

/**
 * The AIM hold's arms clip, per family — rifle only so far (`aim-rifle-
 * arms.glb`, re-authored by `author_aim_rifle.py`: arms raised and levelled,
 * distinct from `idle-armed-rifle-arms`'s lower two-handed carry). A family
 * with no entry here falls back to its idle-armed hold rather than throwing
 * or freezing bare-handed.
 *
 * Shared by NPCs (`activity === 'aim'`) and, since 2026-09-19, by the PLAYER
 * (`input.aiming`) — hence no `NPC_` prefix any more. Janeks: *"we need an
 * AIM pose."* Until then the player's aim branch showed the low-ready idle
 * hold, which mattered beyond looks: `ATTACK_ARMS_CLIP.rifle` is built on
 * the aim pose (see its doc comment), so a player firing a rifle recoiled
 * from a pose they had never been in, and the muzzle pointed down-left at
 * ~-32 deg while the shot went forward.
 */
export const AIM_ARMS_CLIP: Partial<Record<ArmedPoseFamily, ArmsClipName>> = {
  rifle: 'aim-rifle-arms',
  pistol: 'aim-pistol-arms',
};

/**
 * Per-weapon grip for the `aim` pose — needed because `WEAPON_GRIP_OFFSET`
 * above was solved against `idle-armed-<family>-arms`, a different arm
 * orientation, so reusing it here would swing the weapon out of the hand the
 * same way the original (pre-`derive_grips_gltf.py`) player table did.
 * Derived with the SAME `derive_grips_gltf.py` machinery the player's own
 * table came from (re-pointed at `aim-rifle-arms.glb`'s pose instead of
 * `idle-armed-rifle-arms`'s) — not hand-guessed, and not re-deriving the
 * selectors/method, just re-running the verified tool against a different
 * pose clip. Only the three rifle-family keys are covered — anything else
 * requesting `aim` falls back to its idle-armed grip via `resolveNpcWeaponArms`.
 */
export const AIM_GRIP_OFFSET: Readonly<Record<string, {
  position?: readonly [number, number, number];
  rotation?: readonly [number, number, number];
}>> = {
  // Derived by `derive_aim_grips.py` against the shipped `aim-*-arms.glb`
  // clips — NOT the idle hold. A grip is calibrated to hand.R's orientation in
  // one specific clip, so the clip and this table always regenerate together
  // (`author_aim_rifle.py` prints the barrel direction `weapon_grip.AIM_CARRY`
  // then feeds to the solver).
  //
  // Every rifle-family weapon has a row as of 2026-09-18. Before that only
  // three did, and `resolveNpcWeaponArms` fell back to `WEAPON_GRIP_OFFSET` for
  // the rest — an idle-hold grip replayed on the aim pose, i.e. the exact
  // failure this table exists to prevent.
  rifle_bolt:    { position: [ 0.08726,  0.11659,  0.07371], rotation: [ 1.92817, -0.60048,  1.77305] },
  carbine:       { position: [ 0.02019,  0.02982,  0.05160], rotation: [-1.11082, -2.26120, -1.29449] },
  shotgun:       { position: [ 0.27772,  0.18177,  0.11341], rotation: [-1.29975,  0.22492,  1.40590] },
  ak_bullpup:    { position: [ 0.13445,  0.09521,  0.05958], rotation: [ 0.23492,  2.97970, -0.83729] },
  akm:           { position: [ 0.12125,  0.10722,  0.07571], rotation: [ 0.23290, -0.16012,  0.86361] },
  auto_shotgun:  { position: [-0.01344, -0.04103,  0.00479], rotation: [ 0.24575, -0.17628,  0.65896] },
  fn2000:        { position: [ 0.04115, -0.04891,  0.01186], rotation: [ 0.24477,  2.96967, -0.77864] },
  frf2:          { position: [-0.03112, -0.09849, -0.00455], rotation: [ 0.19713,  2.99802, -1.12559] },
  garand:        { position: [ 0.00683, -0.11732, -0.00361], rotation: [ 1.84537, -0.24206,  1.73652] },
  m16:           { position: [ 0.13507,  0.10600,  0.07179], rotation: [ 0.23235,  2.98184, -0.91611] },
  m249:          { position: [ 0.11839, -0.03414,  0.03287], rotation: [ 1.91429, -0.59638,  1.76520] },
  m82:           { position: [ 0.17903,  0.17706,  0.09577], rotation: [ 0.22960, -0.15815,  1.00139] },
  mp4:           { position: [ 0.12800,  0.08504,  0.05693], rotation: [ 0.23409,  2.98045, -0.84512] },
  mp5:           { position: [ 0.17057, -0.00748,  0.02095], rotation: [ 0.23331,  2.98110, -0.94094] },
  mp7:           { position: [ 0.02367,  0.03535,  0.01798], rotation: [ 0.23994,  2.96485, -0.37718] },
  p90:           { position: [ 0.08905,  0.14106,  0.07609], rotation: [ 0.23862,  2.97809, -1.05267] },
  pkm:           { position: [ 0.10428,  0.09815,  0.04774], rotation: [ 1.86426, -0.63857,  1.73540] },
  ppsh:          { position: [ 0.04161,  0.08589,  0.06012], rotation: [ 0.14753,  3.02644, -1.07479] },
  sawed_off:     { position: [ 0.04148,  0.09430,  0.04583], rotation: [-1.21322,  0.64962,  1.36816] },
  scar:          { position: [ 0.13642,  0.09498,  0.07300], rotation: [ 0.23347,  2.98099, -0.89855] },
  scar_alt:      { position: [ 0.13791,  0.05309,  0.06079], rotation: [ 0.23473,  2.97996, -0.87818] },
  scar_scope:    { position: [ 0.12697,  0.08248,  0.06790], rotation: [ 0.23958,  2.97646, -0.92671] },
  svd:           { position: [ 0.11571,  0.14240,  0.07159], rotation: [ 0.22716,  2.98481, -1.02073] },
  ump45:         { position: [ 0.15625,  0.12998,  0.09300], rotation: [ 0.23263,  2.98154, -0.98496] },
  vss:           { position: [ 0.05277,  0.07702,  0.05050], rotation: [ 0.23347,  2.98099, -0.93940] },
  g36:           { position: [ 0.05710,  0.07704,  0.04945], rotation: [ 1.92290, -0.59825,  1.77005] },

  // Pistol family, added 2026-09-19 with `aim-pistol-arms` — derived by
  // `derive_aim_grips.py` against that clip, exactly as the rifle rows were.
  // `snub_revolver` shares `weapon_45cal.glb` with `pistol`, so it shares the
  // row; the solver emits one entry for the two.
  pistol:        { position: [ 0.05250,  0.05051,  0.02658], rotation: [ 0.57543,  0.00140,  0.45522] },
  snub_revolver: { position: [ 0.05250,  0.05051,  0.02658], rotation: [ 0.57543,  0.00140,  0.45522] },
  magnum:        { position: [ 0.01920, -0.02683, -0.02180], rotation: [ 0.57381, -3.13725, -0.47702] },
  mac10:         { position: [ 0.07312,  0.02982,  0.01622], rotation: [ 0.58029, -0.00792,  0.49774] },
  // Premium firearms (2026-10-19), `derive_aim_grips.py`.
  pistol_nickel: { position: [ 0.05250,  0.05051,  0.02658], rotation: [ 0.57543,  0.00140,  0.45522] },
  mp5_urban:     { position: [ 0.17057, -0.00748,  0.02095], rotation: [ 0.23331,  2.98110, -0.94094] },
  scar_woodland: { position: [ 0.13642,  0.09498,  0.07300], rotation: [ 0.23347,  2.98099, -0.89855] },
  m16_rattlecan: { position: [ 0.13507,  0.10600,  0.07179], rotation: [ 0.23235,  2.98184, -0.91611] },
  pkm_arctic:    { position: [ 0.10428,  0.09815,  0.04774], rotation: [ 1.86426, -0.63857,  1.73540] },
  deagle:        { position: [ 0.03176,  0.15311,  0.09470], rotation: [-0.99480, -0.49684, -0.00256] },
  tommygun:      { position: [-0.01034,  0.15116,  0.05550], rotation: [-1.51331, -0.82417, -0.23767] },
  akm_raider:    { position: [ 0.17808,  0.13723,  0.09878], rotation: [ 0.22837, -0.15636,  0.87058] },
};

/**
 * Resolves an NPC's arms clip + weapon grip for a `heldItemKey`/`activity`
 * pair. Three cases, in order:
 *  1. Not a known player weapon key -> not a weapon at all (`NPC_HELD_ITEM`
 *     handles props like `rake` separately, in the constructor below) —
 *     returns null so the caller falls through to the activity's own clips.
 *  2. `activity === 'aim'` and this family has an authored aim clip -> that
 *     clip + `AIM_GRIP_OFFSET`.
 *  3. Otherwise (armed `stand`, or `aim` on an unauthored family) -> the
 *     shared idle-armed hold + the player's own `WEAPON_GRIP_OFFSET`.
 */
function resolveNpcWeaponArms(
  heldItemKey: string,
  activity: NpcActivity,
): { arms: ArmsClipName; url: string; grip: WeaponGrip } | null {
  const family = WEAPON_POSE_FAMILY_BY_KEY[heldItemKey];
  const url = WEAPON_MODEL_BY_KEY[heldItemKey];
  if (!family || !url) return null;
  const { arms, grip } = resolveArmedPose(heldItemKey, family, activity === 'aim');
  return { arms, url, grip };
}

/** A weapon's placement in the hand — the shape both grip tables hold. */
export interface WeaponGrip {
  position?: readonly [number, number, number];
  rotation?: readonly [number, number, number];
}

/**
 * Arms clip + the grip that was calibrated against it, for one armed family.
 *
 * The pairing is the whole point and the reason this is one function rather
 * than two lookups at each call site: the grip is solved per POSE, so a
 * caller that swaps the clip without swapping the grip moves the arms and
 * leaves the weapon behind, floating beside the hand. Both callers — the
 * player's `applyWeaponGrip`/`desiredArmsClip` and the NPC's
 * `resolveNpcWeaponArms` — go through here so they cannot drift apart.
 */
export function resolveArmedPose(
  heldItemKey: string,
  family: ArmedPoseFamily,
  aiming: boolean,
): { arms: ArmsClipName; grip: WeaponGrip } {
  const aimClip = aiming ? AIM_ARMS_CLIP[family] : undefined;
  // Bug found 2026-09-16: this fell back to `{}` (position/rotation both
  // zero — the weapon sits raw at the hand bone's own origin, unrotated)
  // whenever `AIM_GRIP_OFFSET` had no entry for the key, even though the
  // doc comment above always claimed a fall back to the idle-armed grip.
  // Only rifle_bolt/carbine/shotgun ever had an aim-specific grip, so every
  // OTHER rifle-family weapon (the majority of the roster) rendered with a
  // completely unset grip the moment an NPC aimed with one. Falling back to
  // `WEAPON_GRIP_OFFSET` is still an approximation — it was solved against
  // the stand pose's hand orientation, not aim's — but an approximately
  // right grip beats a definitely-wrong raw (0,0,0) one; a real fix needs
  // `derive_aim_grips.py` re-run per key.
  if (aimClip) return { arms: aimClip, grip: AIM_GRIP_OFFSET[heldItemKey] ?? WEAPON_GRIP_OFFSET[heldItemKey] ?? {} };
  return { arms: IDLE_ARMED_CLIP[family], grip: WEAPON_GRIP_OFFSET[heldItemKey] ?? {} };
}

// ─── NPC ambient animation ──────────────────────────────────────────────────

/**
 * Legs/arms clips per `NpcActivity` (declared in ../types — see there for
 * why). The legs entry for `stand`/`work` is the constant standing pose (see
 * `desiredLegsClip`'s note) — only the arms carry the activity.
 *
 * `stand` lists TWO arms clips and cycles them, for the same reason the
 * player's idle does: `idle-1-breathing-arms` animates nothing but
 * `spine.scale` (verified by decoding the shipped GLB — 1 animated channel
 * out of 36), so an NPC holding it alone is a statue with an imperceptible
 * breath. `idle-2-breathing-look-arms` adds a ~0.19 rad head turn, which is
 * what actually reads as "alive" at settlement viewing distance.
 */
const NPC_ACTIVITY_CLIPS: Record<NpcActivity, { legs: LegsClipName; arms: readonly ArmsClipName[] }> = {
  stand: { legs: 'idle-1-breathing', arms: ['idle-1-breathing-arms', 'idle-2-breathing-look-arms'] },
  sit: { legs: 'sit-idle-legs', arms: ['sit-idle-arms'] },
  // `work` is the second hard-coupled legs/arms pair (after `sit`): both
  // halves come from one full-body raking action, and the arms were solved
  // against the legs' own hips sway — see the phase-lock in NpcAnimatorImpl.
  work: { legs: 'work-raking-legs', arms: ['work-raking-arms'] },
  // Unarmed fallback only — an NPC actually needs a weapon for 'aim' to mean
  // anything; see `resolveNpcWeaponArms`, which overrides this whenever
  // `heldItemKey` is a known weapon. Bare-handed 'aim' with no weapon just
  // reads as standing (no dedicated unarmed aim pose exists or is needed).
  aim: { legs: 'idle-1-breathing', arms: ['idle-1-breathing-arms'] },
  // The one activity whose LEGS clip carries the whole pose: `sleep-legs`
  // rotates `root` -90 deg about X to lay the body down, and the arms half
  // inherits that through the parent chain. Swapping only one of the two
  // halves therefore leaves a body either standing with sleeping arms or
  // lying down doing something else — which is exactly why `setActivity`
  // re-bases both slots on one clock rather than touching them independently.
  sleep: { legs: 'sleep-legs', arms: ['sleep-arms'] },
  // Ambient wandering citizens (`WorldObject.wanderRadius`). Unlike every
  // other activity this one is genuinely locomoting, so it reuses the
  // player's own walk pair. The sim keeps a wanderer moving continuously —
  // it never pauses — which is what lets this stay a single steady clip pair
  // like the rest of the table instead of needing a stand/walk state machine
  // and a mid-session legs swap (which would fight the phase-lock below).
  walk: { legs: 'walk-legs', arms: ['walk-arms-unarmed'] },
  // ── arena crowd reactions (author_crowd_reactions.py, 2026-09-19) ────────
  // The THIRD hard-coupled legs/arms pair, after `sit` and `work`: one
  // full-body Blender action exported twice, so the raised fist was authored
  // against this hop's own apex. Its arms array therefore has exactly ONE
  // entry — `update()` cycles a multi-entry array on a clock the legs slot
  // does not follow, which would slide the fist off the jump for the rest of
  // the session. Add variety by lengthening the clip, never by adding a
  // second arms clip here.
  cheer: { legs: 'cheer-legs', arms: ['cheer-arms'] },
  // Arms-only, like `stand` and `aim`: nothing below the waist moves, so both
  // reuse the constant standing legs pose rather than shipping a legs file
  // that would only ever hold still. Both are safe to cycle or swap since
  // neither was solved against moving hips.
  clap: { legs: 'idle-1-breathing', arms: ['clap-arms'] },
  'crossed-arms': { legs: 'idle-1-breathing', arms: ['stand-crossed-arms'] },
  // TERMINAL — the only activity that isn't a steady state. `death-legs`
  // falls and ends supine, same end orientation as `sleep-legs` (both root
  // -90 deg about X); the arms half drops to the same resting pose
  // `sleep-arms` uses. Unlike every other row here, both clips are played
  // `LoopOnce`+`clampWhenFinished` instead of `LoopRepeat` — see the
  // `activity === 'death'` branches in `setActivity`/`setArms` below — and
  // `update()` never cycles this row's `arms` array (there's only one entry
  // and nothing to cycle to). Nothing in this codebase calls
  // `setActivity('death')` yet — no damage/combat event exists in the client
  // runtime — verify it the way every other clip this session got verified,
  // against a map-authored test NPC.
  death: { legs: 'death-legs', arms: ['death-arms'] },
};

/**
 * Activities whose arms may NOT be swapped for the dialog pose.
 *
 * `idle-talking-arms` is authored from the STANDING base pose (verified — it
 * shares keyframe 0 with `idle-1-breathing-arms`). Playing it over a body
 * whose arms were solved against a SPECIFIC legs clip splits that pair for
 * the duration of the conversation: a sitting body snaps its elbows off its
 * thighs into a standing gesture with the legs still folded, and a cheering
 * body keeps hopping with its fist back down at its side.
 *
 * So this is exactly the hard-coupled set. `work` is the odd one out and
 * deliberately absent — the raking arms are phase-locked to their legs, but
 * a raking NPC is a person you walk up to and talk to, and pausing the rake
 * to gesture is the right read; `sit` and `cheer` have no such excuse.
 *
 * `cheer` cannot reach this today (arena spectators are `interactable: false`
 * with no `npcId`, so no dialog can open on one) — but `cheer` is a general
 * `NpcActivity` offered in the map editor, and the first settlement NPC given
 * it would hit the bug this prevents.
 */
const TALKING_LOCKED_ACTIVITIES: readonly NpcActivity[] = ['sit', 'cheer'];

/**
 * Stable 0..1 offset from a string, so two standing NPCs don't look around in
 * perfect unison (which reads as scripted, and is worse than not looking at
 * all). Deterministic on purpose — `Math.random()` here would make the
 * animation untestable and would differ between reloads for no benefit.
 */
function phaseFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return (Math.abs(h) % 1000) / 1000;
}

export interface NpcAnimator {
  /** Advances the mixer. `talking` swaps the arms slot to the dialog pose. Call once per frame with `dt` in seconds. */
  update(dt: number, talking: boolean): void;
  /**
   * Switch what this body is doing, mid-session — walk -> sit -> work -> walk.
   * A no-op when the activity is unchanged, so it is safe to call every frame
   * from a routine driver.
   *
   * This is the capability scripted NPC routines are built on. It did not
   * exist before 2026-09-11: `activity` was a constructor argument, and the
   * legs clip, the arms set and the held item's grip were all resolved once
   * and had no path to change. Ambient citizens consequently walked
   * continuously and never paused — not a stylistic choice, just the only
   * thing the animator could express.
   *
   * BOTH SLOTS RE-BASE ON ONE CLOCK. `sit` and `work` are hard-coupled pairs
   * (their arms were solved against those exact legs' hip motion), so the
   * shared `cycleT` is reset here and the incoming legs and arms both start
   * from it. Swapping one slot alone — or letting the new legs start at 0
   * while the arms continue from the old clock — slides a coupled pair apart
   * for the rest of the session, which is precisely the phase-lock hazard the
   * constructor's own comment warns about.
   */
  setActivity(activity: NpcActivity): void;
  /** Damage-taken reaction — see `PlayerAnimator.playHitReact`'s doc comment. */
  playHitReact(): void;
  /** Attack one-shot — see `PlayerAnimator.playAttack`'s doc comment. */
  playAttack(): void;
  dispose(): void;
}

const NOOP_NPC_ANIMATOR: NpcAnimator = {
  update: () => {}, setActivity: () => {}, playHitReact: () => {}, playAttack: () => {}, dispose: () => {},
};

/**
 * Ambient animation for a placed NPC body. Far simpler than the player's
 * runtime: an NPC never moves, so there is no locomotion state machine — just
 * one steady activity pair, plus a talking override on the arms slot while
 * the player has this NPC's dialog open.
 *
 * **Returns a no-op animator unless `root` actually contains a `SkinnedMesh`**,
 * so an NPC body pointing at an unrigged model silently stays static rather
 * than throwing — deliberate, so the settlement can mix rigged and unrigged
 * bodies rather than forcing every NPC onto one mesh to get any animating.
 *
 * As of 2026-09-09 all 24 character bodies — the eight `char-*` player presets
 * and the fifteen `npc-*` bodies — carry the 20-bone skeleton, so this
 * fallback no longer fires for any placed NPC. It is kept because the silence
 * is the danger: an unrigged re-export would produce statues with no error.
 * `character-models.test.ts` asserts the skins, weights and bone NAMES from
 * the shipped bytes so that regression fails a test instead of shipping.
 */
export function createNpcAnimator(
  root: THREE.Object3D,
  activity: NpcActivity = 'stand',
  /** De-synchronizes idle cycling between NPCs — pass the NPC's id. */
  phaseKey = '',
  /** `WorldObject.heldItemKey` — a prop shown parented to `hand.R`, see `NPC_HELD_ITEM`. Unknown/absent key holds nothing. */
  heldItemKey?: string,
  /** `WorldObject.equippedKeys` — items this body WEARS. Garment keys are
   *  skinned to its own skeleton, TORSO keys rigidly attached to `chest`. */
  equippedKeys?: readonly string[],
): NpcAnimator {
  let skinned: THREE.SkinnedMesh | null = null;
  root.traverse((o) => {
    if (!skinned && (o as THREE.SkinnedMesh).isSkinnedMesh) skinned = o as THREE.SkinnedMesh;
  });
  if (!skinned) return NOOP_NPC_ANIMATOR;
  return new NpcAnimatorImpl(root, skinned, activity, phaseKey, heldItemKey, equippedKeys);
}

class NpcAnimatorImpl implements NpcAnimator {
  private readonly mixer: THREE.AnimationMixer;
  private legsAction: THREE.AnimationAction | null = null;
  private armsActions = new Map<ArmsClipName, THREE.AnimationAction>();
  private currentArmsName: ArmsClipName | null = null;
  private disposed = false;

  /** The activity's own arms clips, cycled when there's more than one. */
  private idleArms: readonly ArmsClipName[];
  private cycleT: number;
  /** Current activity — mutable, so `setActivity` can switch it mid-session. */
  private activity: NpcActivity;
  private readonly heldItemKey: string | undefined;
  /**
   * The attached weapon/prop, kept so `setActivity` can re-seat it. The grip
   * is calibrated per POSE (`AIM_GRIP_OFFSET` vs `WEAPON_GRIP_OFFSET`),
   * so a stand -> aim switch that moves the arms without moving the weapon
   * leaves the rifle floating beside the hand.
   */
  private heldModel: THREE.Object3D | null = null;
  /** The bone `heldModel` is parented to — `applyGrip` re-anchors against it, `dropHeldWeapon` uses it as the "is something actually held" test. */
  private heldModelBone: THREE.Bone | null = null;
  /** This NPC body's own palm, measured once at construction — see `anchoredGripPosition`. */
  private palmLocal: THREE.Vector3 | null = null;
  /** Worn items (see `WorldObject.equippedKeys`), kept so `dispose` detaches them. */
  private readonly wornModels: THREE.Object3D[] = [];
  private bodyMask: BodyRegionMask | null = null;

  // ---- hit-react one-shot (see HIT_REACT_ARMS_CLIP) ----
  private hitReactAction: THREE.AnimationAction | null = null;
  private attackAction: THREE.AnimationAction | null = null;

  constructor(
    private readonly root: THREE.Object3D,
    skinned: THREE.SkinnedMesh,
    activity: NpcActivity,
    phaseKey: string,
    heldItemKey: string | undefined,
    equippedKeys: readonly string[] | undefined,
  ) {
    this.mixer = new THREE.AnimationMixer(root);
    this.mixer.addEventListener('finished', this.onHitReactFinished);
    this.mixer.addEventListener('finished', this.onAttackFinished);
    this.activity = activity;
    this.heldItemKey = heldItemKey;
    const { legs } = NPC_ACTIVITY_CLIPS[activity];
    // A held weapon overrides the activity's own arms clip (single clip, no
    // cycling) so the grip and the pose it was solved against always travel
    // together — see `resolveNpcWeaponArms`. Anything else (no held item, or
    // a non-weapon prop like `rake`) keeps the activity's normal arms.
    const weaponArms = heldItemKey ? resolveNpcWeaponArms(heldItemKey, activity) : null;
    const arms: readonly ArmsClipName[] = weaponArms ? [weaponArms.arms] : NPC_ACTIVITY_CLIPS[activity].arms;
    this.idleArms = arms;
    this.cycleT = phaseFromId(phaseKey) * IDLE_CYCLE_SECONDS * arms.length;

    this.bodyMask = BodyRegionMask.create(skinned);
    const handBoneOrNull = skinned.skeleton.getBoneByName(HAND_R_BONE) ?? null;
    this.palmLocal = handBoneOrNull ? measureBoneLocalCentroid(skinned, handBoneOrNull) : null;

    if (heldItemKey) {
      const handBone = handBoneOrNull;
      // Weapon key (rifle/pistol/knife/axe/hockey_stick) -> the player's own
      // model + grip, re-derived for 'aim' where an aim pose exists.
      // Anything else -> `NPC_HELD_ITEM`'s NPC-only props (e.g. `rake`).
      const held = weaponArms ?? (NPC_HELD_ITEM[heldItemKey] ? { url: NPC_HELD_ITEM[heldItemKey]!.url, grip: NPC_HELD_ITEM[heldItemKey]! } : null);
      if (held && handBone) {
        void loadPropModel(held.url).then((model) => {
          if (this.disposed) return;
          this.heldModel = model;
          this.heldModelBone = handBone;
          // Re-read the grip from the CURRENT activity rather than closing
          // over `held` — `setActivity` may have run while this was loading,
          // and the async path must not re-apply a stale pose's offsets.
          this.applyGrip();
          attachToBone(handBone, model);
        }).catch((err) => console.error(`[character-animations] NPC held item "${heldItemKey}" failed`, err));
      }
    }

    // ── worn equipment ──────────────────────────────────────────────────
    // Exactly the two paths the player uses, chosen by which table the key is
    // in — deliberately NOT a third NPC-only mechanism. A garment is skinned
    // to THIS body's skeleton (so its sleeves follow this body's arms); a
    // TORSO vest is rigidly parented to `chest`, which carries no animated
    // transform. An unrecognised key is ignored, like `heldItemKey`'s is.
    const wornGarmentKeys: string[] = [];
    const drawnKeys = new Set(drawnGarmentKeys(equippedKeys ?? []));
    for (const key of equippedKeys ?? []) {
      if (GARMENT_KEYS.includes(key) && !drawnKeys.has(key)) continue;
      if (GARMENT_KEYS.includes(key)) {
        void loadGarmentRoot(garmentUrl(key)).then((loaded) => {
          if (this.disposed) return;
          const garment = bindGarmentToSkeleton(SkeletonUtils.clone(loaded), skinned.skeleton);
          if (!garment) return;
          applyGarmentColors(garment, key);
          root.add(garment);
          this.wornModels.push(garment);
          wornGarmentKeys.push(key);
          this.bodyMask?.setCovered(coveredRegions(wornGarmentKeys));
        }).catch((err) => console.error(`[character-animations] NPC garment "${key}" failed`, err));
        continue;
      }
      if (isBackpackKey(key)) {
        const bagBone = skinned.skeleton.getBoneByName(CHEST_BONE);
        if (!bagBone) continue;
        void buildBackpackModel(key, loadPropModel).then((built) => {
          if (!built) return;
          if (this.disposed) { disposeBackpack(built.model); return; }
          built.model.position.set(...fixedMetreOffset(built.offset, bagBone));
          attachToBone(bagBone, built.model);
          this.wornModels.push(built.model);
        }).catch((err) => console.error(`[character-animations] NPC backpack "${key}" failed`, err));
        continue;
      }
      const torsoUrl = TORSO_MODEL_BY_KEY[key];
      if (!torsoUrl) continue;
      const chestBone = skinned.skeleton.getBoneByName(CHEST_BONE);
      if (!chestBone) continue;
      void loadPropModel(torsoUrl).then((model) => {
        if (this.disposed) return;
        const offset = TORSO_GRIP_OFFSET[key];
        if (offset?.position) model.position.set(...fixedMetreOffset(offset.position, chestBone));
        if (offset?.rotation) model.rotation.set(...offset.rotation);
        attachToBone(chestBone, model);
        this.wornModels.push(model);
      }).catch((err) => console.error(`[character-animations] NPC torso "${key}" failed`, err));
    }

    // A seated or cheering NPC keeps its own arms even mid-conversation — see
    // TALKING_LOCKED_ACTIVITIES. Standing and working NPCs have no such conflict.
    const armsToLoad: ArmsClipName[] = TALKING_LOCKED_ACTIVITIES.includes(activity)
      ? [...arms]
      : [...arms, 'idle-talking-arms'];

    loadLegsClip(legs)
      .then((clip) => {
        if (this.disposed) return;
        this.legsAction = this.mixer.clipAction(clip);
        // 'death' is terminal — falls once and holds the last frame, never
        // loops. Every other activity here is a steady state.
        if (activity === 'death') {
          this.legsAction.setLoop(THREE.LoopOnce, 1);
          this.legsAction.clampWhenFinished = true;
        } else {
          this.legsAction.setLoop(THREE.LoopRepeat, Infinity);
        }
        this.legsAction.play();
        // Phase-lock to the shared cycleT clock. The legs and arms clips load
        // (and therefore start) at independent times; for the hard-coupled
        // pairs (sit, work-raking — arms solved against these legs' own hips
        // motion) an async-load offset would slide the halves apart for the
        // whole session. Starting both slots at cycleT % duration pins them
        // to one clock regardless of which loaded first. NOT for 'death' —
        // a terminal one-shot must always start its fall from frame 0, never
        // from `phaseFromId`'s per-NPC offset into it.
        if (activity !== 'death') this.legsAction.time = this.cycleT % clip.duration;
      })
      .catch((err) => console.error(`[character-animations] NPC legs clip "${legs}" failed`, err));

    for (const name of armsToLoad) {
      loadArmsClip(name)
        .then((clip) => { if (!this.disposed) this.armsActions.set(name, this.mixer.clipAction(clip)); })
        .catch((err) => console.error(`[character-animations] NPC arms clip "${name}" failed`, err));
    }
  }

  /**
   * Position the held model for the CURRENT activity's grip.
   *
   * Split out because two independent paths need it: the async model load,
   * and every `setActivity` that changes which grip table applies (rifle
   * `stand` uses `WEAPON_GRIP_OFFSET`, rifle `aim` uses `AIM_GRIP_OFFSET`
   * — see `resolveNpcWeaponArms`). Offsets are SET, never accumulated, so
   * repeated calls are idempotent.
   */
  private applyGrip(): void {
    if (!this.heldModel || !this.heldItemKey) return;
    const weapon = resolveNpcWeaponArms(this.heldItemKey, this.activity);
    const grip = weapon ? weapon.grip : NPC_HELD_ITEM[this.heldItemKey];
    if (!grip) return;
    // NOT divided by the bone's world scale: a grip position is already a
    // bone-local vector in rig units, so that scale multiplying it is the
    // point — see `attachToBone`'s doc comment on why only the SCALE is
    // cancelled. Re-anchored onto this body's own palm, like the first attach.
    const position = this.heldModelBone
      ? anchoredGripPosition(grip.position ?? [0, 0, 0], this.palmLocal, this.heldModelBone)
      : (grip.position ?? [0, 0, 0]);
    this.heldModel.position.set(...position);
    this.heldModel.rotation.set(...(grip.rotation ?? [0, 0, 0]));
  }

  /**
   * Detaches a held weapon/prop from the hand and drops it straight down to
   * ground level, called once when `setActivity('death')` fires — "weapon
   * nearby on ground" from the original brief. `Object3D.attach()` reparents
   * onto the same stable object-root the character model itself hangs off
   * (never `this.root`'s own bone chain, which is about to freeze on
   * `death`'s last frame anyway) while preserving the CURRENT world
   * transform — no manual matrix decomposition needed. Scale is then reset
   * to the authored real-world value `attachToBone` stashed at attach time,
   * since the new parent's world scale is (in general) different from
   * `hand.R`'s — reusing that stashed number instead of re-deriving it is
   * exactly the fix this session's grip-scale bug needed everywhere else.
   *
   * First-pass approximation: keeps the weapon's in-hand rotation rather
   * than tipping it to rest flat on the ground — good enough to read as
   * "dropped nearby", not a physically simulated fall.
   */
  private dropHeldWeapon(): void {
    if (!this.heldModel || !this.heldModelBone) return;
    const model = this.heldModel;
    const groundParent = this.root.parent ?? this.root;
    const authoredScale = (model.userData.authoredScale as number | undefined) ?? model.scale.x;
    groundParent.attach(model); // reparents, preserving world transform; scale ends up renormalized for the new parent
    model.position.y = this.root.position.y; // straight down to the character root's own ground level
    model.scale.setScalar(authoredScale); // real-world size again, not whatever attach() computed against the new parent
    this.heldModel = null;
    this.heldModelBone = null;
  }

  /** Arms clips this activity needs loaded, talking override included where legal. */
  private armsClipsFor(activity: NpcActivity): { arms: readonly ArmsClipName[]; toLoad: ArmsClipName[] } {
    // 'death' must always use NPC_ACTIVITY_CLIPS.death's own arms clip.
    // `resolveNpcWeaponArms` has no notion of 'death' — its "otherwise" case
    // falls through to the weapon's ordinary idle-hold clip — and
    // `heldItemKey` is still set here even though `dropHeldWeapon()` already
    // detached the mesh (only the visual model needed to leave the hand, not
    // this bookkeeping field). Left ungated, every armed NPC's death arms
    // silently became a one-shot idle-hold clip instead of death-arms: legs
    // fell and settled supine correctly, but the arms slot played a
    // breathing-idle clip (authored to end where it starts, for looping)
    // clamped after a single pass -- reads as the torso pivoting back then
    // returning on an already-fallen body, not a fall.
    const weaponArms = activity !== 'death' && this.heldItemKey ? resolveNpcWeaponArms(this.heldItemKey, activity) : null;
    const arms: readonly ArmsClipName[] = weaponArms ? [weaponArms.arms] : NPC_ACTIVITY_CLIPS[activity].arms;
    // Same seated exclusion the constructor applies: `idle-talking-arms` is
    // authored from the STANDING base pose, so on a sitting body it snaps the
    // arms into a standing gesture over folded legs.
    // See TALKING_LOCKED_ACTIVITIES — the same exemption the constructor applies.
    return {
      arms,
      toLoad: TALKING_LOCKED_ACTIVITIES.includes(activity) ? [...arms] : [...arms, 'idle-talking-arms'],
    };
  }

  setActivity(activity: NpcActivity): void {
    if (this.disposed || activity === this.activity) return;
    this.activity = activity;
    if (activity === 'death') this.dropHeldWeapon();

    const { arms, toLoad } = this.armsClipsFor(activity);
    this.idleArms = arms;
    // Re-base the shared clock. Both slots below start from this same value,
    // which is what keeps a hard-coupled pair (sit / work-raking) aligned
    // across the switch — see this method's doc comment on the interface.
    this.cycleT = 0;
    // Force the next update() to re-select and crossfade, even if the new
    // activity's first arms clip happens to share a name with the old one's.
    const previousArms = this.currentArmsName;
    this.currentArmsName = null;
    if (previousArms && !arms.includes(previousArms) && previousArms !== 'idle-talking-arms') {
      this.armsActions.get(previousArms)?.fadeOut(CROSSFADE_SECONDS);
    }

    for (const name of toLoad) {
      if (this.armsActions.has(name)) continue;
      void loadArmsClip(name)
        .then((clip) => { if (!this.disposed) this.armsActions.set(name, this.mixer.clipAction(clip)); })
        .catch((err) => console.error(`[character-animations] NPC arms clip "${name}" failed`, err));
    }

    this.applyGrip();

    const { legs } = NPC_ACTIVITY_CLIPS[activity];
    void loadLegsClip(legs)
      .then((clip) => {
        if (this.disposed || this.activity !== activity) return;   // superseded mid-load
        const next = this.mixer.clipAction(clip);
        if (next === this.legsAction) return;
        next.reset();
        // 'death' is terminal — see NPC_ACTIVITY_CLIPS.death's comment.
        if (activity === 'death') {
          next.setLoop(THREE.LoopOnce, 1);
          next.clampWhenFinished = true;
        } else {
          next.setLoop(THREE.LoopRepeat, Infinity);
        }
        // `cycleT` was reset to 0 above, so this is frame 0 for 'death' too —
        // a terminal one-shot must always start its fall from the beginning.
        next.time = this.cycleT % clip.duration;
        next.play();
        if (this.legsAction) this.legsAction.crossFadeTo(next, CROSSFADE_SECONDS, true);
        else next.fadeIn(CROSSFADE_SECONDS);
        this.legsAction = next;
      })
      .catch((err) => console.error(`[character-animations] NPC legs clip "${legs}" failed`, err));
  }

  update(dt: number, talking: boolean): void {
    if (this.disposed) return;
    this.mixer.update(dt);
    this.cycleT += dt;
    // A hit-react or attack one-shot owns the arms slot until its 'finished'
    // handler ends it — see playHitReact / playAttack.
    if (this.hitReactAction || this.attackAction) return;
    // Falls back to the activity's own arms when there's no talking clip
    // loaded for it (a seated NPC, by design above). A dead body never
    // responds to `talking` — a dialog system addressing a corpse must not
    // snap it back into a standing talk gesture.
    const want = talking && this.activity !== 'death' && this.armsActions.has('idle-talking-arms')
      ? 'idle-talking-arms'
      : this.idleArms[Math.floor(this.cycleT / IDLE_CYCLE_SECONDS) % this.idleArms.length]!;
    this.setArms(want);
  }

  /**
   * Damage-taken reaction — see `PlayerAnimator.playHitReact`'s doc comment,
   * same clip table and one-shot shape, just this class's own arms-slot
   * bookkeeping (`setArms`'s `currentArmsName`/crossfade) instead of the
   * player animator's. Family comes from `heldItemKey` the same way
   * `resolveNpcWeaponArms` derives it, falling back to `unarmed` for an
   * NPC holding nothing or an unrecognized prop (e.g. `rake`).
   */
  playHitReact(): void {
    if (this.disposed || this.hitReactAction) return;
    // A corpse does not flinch. Beyond the obvious, this would actively
    // resurrect it: `onHitReactFinished` clears `currentArmsName`, so the next
    // `update()` calls `setArms('death-arms')` — and the `death` branch there
    // `reset()`s without seeking `cycleT`, which restarts the clip at frame 0,
    // i.e. standing. The body would flinch, stand up and die a second time.
    if (this.activity === 'death') return;
    this.attackAction = null; // a hit interrupts an in-flight swing — see the player animator's note
    const family = this.heldItemKey ? WEAPON_POSE_FAMILY_BY_KEY[this.heldItemKey] : undefined;
    const name = HIT_REACT_ARMS_CLIP[family ?? 'unarmed'];
    const action = this.armsActions.get(name);
    if (!action) {
      // Not loaded (rare — hit-react clips aren't in this class's eager set).
      // Kick off the load so the NEXT hit lands; this one is silently skipped.
      void loadArmsClip(name)
        .then((clip) => { if (!this.disposed) this.armsActions.set(name, this.mixer.clipAction(clip)); })
        .catch((err) => console.error(`[character-animations] NPC hit-react clip "${name}" failed`, err));
      return;
    }
    const from = this.currentArmsName ? this.armsActions.get(this.currentArmsName) : null;
    this.hitReactAction = action;
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.play();
    if (from) from.crossFadeTo(action, CROSSFADE_SECONDS, true);
    else action.fadeIn(CROSSFADE_SECONDS);
    this.currentArmsName = null; // sentinel: mid one-shot, like every setActivity switch
  }

  /**
   * Attack one-shot — see `PlayerAnimator.playAttack`'s doc comment for the
   * clip table, the unarmed no-op and the hit-react-wins precedence. Family
   * comes from `heldItemKey`, same as `playHitReact`.
   *
   * A dead NPC never attacks, for the same reason it never flinches: the
   * one-shot's completion handler clears `currentArmsName`, which sends the
   * next `update()` into `setArms('death-arms')` — and that path `reset()`s
   * without seeking, restarting the death from its standing frame 0.
   */
  playAttack(): void {
    if (this.disposed || this.hitReactAction || this.attackAction) return;
    if (this.activity === 'death') return;
    const family = this.heldItemKey ? WEAPON_POSE_FAMILY_BY_KEY[this.heldItemKey] : undefined;
    const name = family ? ATTACK_ARMS_CLIP[family] : undefined;
    if (!name) return; // unarmed / non-weapon prop — no asset, see ATTACK_ARMS_CLIP
    const action = this.armsActions.get(name);
    if (!action) {
      void loadArmsClip(name)
        .then((clip) => { if (!this.disposed) this.armsActions.set(name, this.mixer.clipAction(clip)); })
        .catch((err) => console.error(`[character-animations] NPC attack clip "${name}" failed`, err));
      return;
    }
    const from = this.currentArmsName ? this.armsActions.get(this.currentArmsName) : null;
    this.attackAction = action;
    action.reset();
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.play();
    if (from) from.crossFadeTo(action, CROSSFADE_SECONDS, true);
    else action.fadeIn(CROSSFADE_SECONDS);
    this.currentArmsName = null; // sentinel: mid one-shot, like every setActivity switch
  }

  private onAttackFinished = (e: { action: THREE.AnimationAction }): void => {
    if (this.attackAction && e.action === this.attackAction) {
      this.attackAction = null;
      this.currentArmsName = null; // force the next update() to re-derive and crossfade back to steady state
    }
  };

  private onHitReactFinished = (e: { action: THREE.AnimationAction }): void => {
    if (this.hitReactAction && e.action === this.hitReactAction) {
      this.hitReactAction = null;
      this.currentArmsName = null; // force the next update() to re-derive and crossfade back to steady state
    }
  };

  private setArms(name: ArmsClipName): void {
    if (name === this.currentArmsName) return;
    const action = this.armsActions.get(name);
    if (!action) return; // not loaded yet — retried next frame
    const from = this.currentArmsName ? this.armsActions.get(this.currentArmsName) : null;
    action.reset();
    // 'death' is terminal (see NPC_ACTIVITY_CLIPS.death) — falls once from
    // frame 0 and holds, never loops and never starts from the shared
    // cycleT clock the way every other activity's arms do.
    if (this.activity === 'death') {
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;
    } else {
      action.setLoop(THREE.LoopRepeat, Infinity);
    }
    action.play();
    // Activity arms start on the shared cycleT clock so they stay phase-locked
    // to the legs slot (see the legs load above). The talking override is a
    // gesture, not a legs partner — it starts from its own beginning.
    if (name !== 'idle-talking-arms' && this.activity !== 'death') {
      action.time = this.cycleT % action.getClip().duration;
    }
    if (from) from.crossFadeTo(action, CROSSFADE_SECONDS, true);
    else action.fadeIn(CROSSFADE_SECONDS);
    this.currentArmsName = name;
  }

  dispose(): void {
    this.disposed = true;
    for (const worn of this.wornModels) worn.removeFromParent();
    this.wornModels.length = 0;
    this.bodyMask?.restore();
    this.mixer.removeEventListener('finished', this.onHitReactFinished);
    this.mixer.removeEventListener('finished', this.onAttackFinished);
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.root);
  }
}
